/**
 * /darts — the page: set-up, the console's layout, the throw, the bot, the
 * scorer for a real board, the statistics and saving.
 *
 * The rules live in rules.js (a match = set-up + events, the state is their
 * replay), the board's pixels in board.js, the bot in ai.js. This file only
 * turns presses into events and the state into the screen.
 */

import { createMatch, apply, replay, hint, target, roundsOf, keypadKind, impossibleVisit, avg3, first9, checkoutPct, mpr, soloOnly, CRICKET } from './rules.js';
import { hitAt, aimPoint, code, gauss, MM } from './geometry.js';
import { route, oneDartFinish } from './checkout.js';
import { throwDart, sigmaFor, levelAvg, LEVELS } from './ai.js';
import { createBoard, PLAYER_COLORS } from './board.js';
import { createSound } from './sound.js';

const SLUG = 'darts';
const CFG = window.__DT_CFG || {};
const U = CFG.ui || {};
const PRO = CFG.pro || {};
const $ = (id) => document.getElementById(id);
const root = $('dtApp');

/* ── text ── */
function L(key, vars) {
    let s = U[key] != null ? String(U[key]) : key;
    if (vars) Object.keys(vars).sort((a, b) => b.length - a.length).forEach((k) => { s = s.split(':' + k).join(String(vars[k])); });
    return s;
}
const nf = (() => { try { return new Intl.NumberFormat(window.__intlLocale ? window.__intlLocale() : undefined, { maximumFractionDigits: 1, minimumFractionDigits: 1 }); } catch (_) { return { format: (n) => n.toFixed(1) }; } })();
const f1 = (n) => nf.format(Math.round(n * 10) / 10);
// a bed as players write it (T20, D16, 25) — the bull in the page's language
const cl = (s, m) => { const c = code(s, m); return c === 'Bull' ? L('bull') : c; };
const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* ── telemetry (the site's doors) ── */
function stage(n, d) { try { window.__stage && window.__stage(n, d || {}); } catch (_) {} }
function feature(action, d) { try { window.__ae && window.__ae('feature_use', Object.assign({ action }, d || {})); } catch (_) {} }

/* ── settings and records (per-viewer conveniences: the site's settings store) ── */
const TS = window.__toolSettings;
const load = (k, fb) => { try { return TS ? TS.load(SLUG, k, fb) : fb; } catch (_) { return fb; } };
const save = (k, v) => { try { TS && TS.save(SLUG, k, v); } catch (_) {} };

const settings = Object.assign({ sound: true, caller: false, hints: true, throwStyle: 'aim', theme: 'classic' }, load('settings', {}) || {});
const setup = Object.assign({
    mode: 'x01', opp: 'bot', level: 'l3', custom: 70, count: 2, names: [], where: 'screen', hand: 'mid',
    opts: { x01: { start: 501, outRule: 'double', inRule: 'straight', legs: 1 }, cricket: { cut: 0, legs: 1 }, shanghai: { rounds: 7 }, atc: { variant: 'any' }, checkouts: { drill: 'random' } },
}, load('setup', {}) || {});
['x01', 'cricket', 'shanghai', 'atc', 'checkouts'].forEach((m) => { if (!setup.opts[m]) setup.opts[m] = {}; });

// the hand: sway (beaten by timing — hold until it calms) and a spread no timing removes.
// Aiming at T20 with good timing: easy ≈ 38 % trebles, normal ≈ 24 %, shaky ≈ 12 %
// (2-D normal against the T20 bed: ±4 mm radially, ±16 mm across at 103 mm).
const HAND = { easy: { amp: 0.030, res: 0.038, sweep: 1900 }, mid: { amp: 0.050, res: 0.055, sweep: 1450 }, hard: { amp: 0.075, res: 0.080, sweep: 1050 } };

/* ── state ── */
const app = {
    taught: !!load('taught', 0),
    // online (a match by link): the room, our role, our seat; rq — the other side's events waiting their turn
    net: null, netRole: null, netCode: null, me: null, netState: '', rq: [], remoteFlying: false, finished: false,
    cfg: null, events: [], s: null, phase: 'idle', assisted: false, botTimer: 0, pending: null,
    inMode: 'total', typed: '', ask: null, layout: 'tall', startedAt: 0, firstThrow: false, shown: null,
};
const board = createBoard($('dtCanvas'), { reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches });
const snd = createSound();
snd.enabled = settings.sound;
snd.caller = settings.caller;

/* ═══════════════════════════ premium ═══════════════════════════ */
function proLocked() {
    if (!PRO.offered) return false;
    try { return !(window.__isProNow && window.__isProNow(false)); } catch (_) { return true; }
}
function proNote(el, text, from) {
    el.hidden = false;
    el.textContent = text + ' ';
    const a = document.createElement('a');
    a.href = PRO.url || '#';
    a.textContent = L('pro_cta');
    a.addEventListener('click', () => feature('dt_pro_click', { from }));
    el.appendChild(a);
}

/* ═══════════════════════════ set-up ═══════════════════════════ */
function seg(el, onPick, attr = 'aria-checked') {
    if (!el) return;
    el.addEventListener('click', (e) => {
        const b = e.target.closest('.tc-seg-b');
        if (!b || !el.contains(b)) return;
        setSeg(el, b.dataset.v, attr);
        onPick(b.dataset.v);
    });
}
function setSeg(el, v, attr = 'aria-checked') {
    if (!el) return;
    el.querySelectorAll('.tc-seg-b').forEach((b) => {
        const on = String(b.dataset.v) === String(v);
        b.classList.toggle('on', on);
        b.setAttribute(attr, on ? 'true' : 'false');
    });
}
function radios(el, sel, key, onPick) {
    el.addEventListener('click', (e) => {
        const b = e.target.closest(sel);
        if (!b || !el.contains(b)) return;
        onPick(b.dataset[key], b);
    });
}
function markRadio(el, sel, key, v) {
    el.querySelectorAll(sel).forEach((b) => b.setAttribute('aria-checked', b.dataset[key] === v ? 'true' : 'false'));
}

function modeOpts(m) { return setup.opts[m] || {}; }

function renderSetup() {
    const m = setup.mode;
    markRadio($('dtModes'), '.dt-mode', 'mode', m);
    $('dtDesc').textContent = L('desc_' + m);
    document.querySelectorAll('#dtOpts .dt-row').forEach((r) => { r.hidden = r.dataset.for.split(' ').indexOf(m) < 0; });
    const o = modeOpts(m);
    document.querySelectorAll('#dtOpts .dt-row-sw').forEach((r) => { r.hidden = r.dataset.for.split(' ').indexOf(m) < 0; });
    $('dtDoubleIn').checked = (setup.opts.x01 || {}).inRule === 'double';
    document.querySelectorAll('#dtOpts [data-opt]').forEach((g) => {
        const k = g.dataset.opt;
        let v = o[k];
        if (k === 'cut') v = o.cut ? 1 : 0;
        if (v != null) setSeg(g, v);
    });
    const solo = soloOnly(m);
    $('dtOppRow').hidden = solo;
    $('dtSoloNote').hidden = !solo;
    const opp = solo ? 'solo' : setup.opp;
    setSeg($('dtOpp'), setup.opp);
    $('dtLevelBox').hidden = opp !== 'bot';
    $('dtFriendsBox').hidden = opp !== 'friends';
    $('dtOnlineBox').hidden = opp !== 'online';
    if (document.activeElement !== $('dtMyName')) $('dtMyName').value = setup.myName || '';
    // the daily challenge already played today: say so (and it can be played again, without a record)
    const dn = $('dtDailyNote');
    const dk = (setup.opts.checkouts || {}).drill === 'daily' && m === 'checkouts' ? (readStats().daily || {})[dailyKey()] : null;
    dn.hidden = dk == null;
    if (dk != null) dn.textContent = L('drill_daily_done', { n: dk, of: 10 });
    markRadio($('dtLevels'), '.dt-level', 'level', setup.level);
    $('dtCustom').hidden = setup.level !== 'custom';
    $('dtLevelCap').textContent = setup.level === 'custom' ? '' : L('level_' + setup.level) + ' · ' + L('level_hint', { n: levelAvg(setup.level) });
    $('dtCustomAvg').value = setup.custom;
    $('dtCustomOut').textContent = setup.custom;
    setSeg($('dtCount'), setup.count);
    document.querySelectorAll('#dtNames .dt-name').forEach((inp) => {
        const i = +inp.dataset.i;
        inp.hidden = i >= setup.count;
        if (document.activeElement !== inp) inp.value = setup.names[i] || '';
    });
    setSeg($('dtWhere'), setup.where);
    setSeg($('dtHand'), setup.hand);
    $('dtHandBox').hidden = setup.where === 'real';
    $('dtRealNote').hidden = setup.where !== 'real';
    root.dataset.input = setup.where;
    // the board explains the game: its targets lit before it starts
    if (root.dataset.state === 'setup') board.setHighlight(previewBeds(m));
    $('dtResume').hidden = !readSave();
}

function previewBeds(m) {
    switch (m) {
        case 'cricket': return CRICKET.map((n) => ({ s: n, m: 0 }));
        case 'sector20': return [{ s: 20, m: 0 }];
        case 'countup': return [{ s: 20, m: 3 }];
        case 'x01': return [{ s: 20, m: 3 }, { s: 20, m: 2 }];
        case 'biground': case 'shanghai': case 'atc': return [{ s: 1, m: 0 }];
        case 'bobs27': return [{ s: 1, m: 2 }];
        case 'checkouts': return [{ s: 16, m: 2 }, { s: 20, m: 2 }];
        default: return null;
    }
}

function saveSetup() { save('setup', setup); }

function bindSetup() {
    radios($('dtModes'), '.dt-mode', 'mode', (v) => {
        setup.mode = v; saveSetup(); renderSetup(); fitSoon(); feature('dt_mode', { mode: v });
        const pop = $('dtModePop');
        if ($('dtModes').parentElement === pop) { try { window.__anchoredPopover ? window.__anchoredPopover.close(pop) : pop.hidePopover(); } catch (_) {} placeModes(true); }
    });
    document.querySelectorAll('#dtOpts [data-opt]').forEach((g) => seg(g, (v) => {
        const k = g.dataset.opt;
        const o = setup.opts[setup.mode] || (setup.opts[setup.mode] = {});
        o[k] = k === 'cut' ? (v === '1' ? 1 : 0) : (/^\d+$/.test(v) ? +v : v);
        saveSetup();
    }));
    seg($('dtOpp'), (v) => { setup.opp = v; saveSetup(); renderSetup(); fitSoon(); });
    radios($('dtLevels'), '.dt-level', 'level', (v) => {
        const note = $('dtCustomPro');
        if (v === 'custom' && proLocked()) { proNote(note, L('pro_custom_note'), 'custom_bot'); feature('dt_custom_locked'); return; }
        note.hidden = true;
        setup.level = v; saveSetup(); renderSetup();
    });
    $('dtCustomAvg').addEventListener('input', (e) => { setup.custom = +e.target.value; $('dtCustomOut').textContent = setup.custom; saveSetup(); });
    seg($('dtCount'), (v) => { setup.count = +v; saveSetup(); renderSetup(); });
    document.querySelectorAll('#dtNames .dt-name').forEach((inp) => inp.addEventListener('input', () => {
        setup.names[+inp.dataset.i] = inp.value.trim().slice(0, 16); saveSetup();
    }));
    seg($('dtWhere'), (v) => { setup.where = v; saveSetup(); renderSetup(); feature('dt_where', { where: v }); });
    $('dtMyName').addEventListener('input', (e) => { setup.myName = e.target.value.trim().slice(0, 16); saveSetup(); });
    seg($('dtHand'), (v) => { setup.hand = v; saveSetup(); });
    $('dtStart').addEventListener('click', () => {
        snd.unlock();
        if (setup.opp === 'online' && !soloOnly(setup.mode)) startOnlineHost(); else startMatch();
    });
    $('dtJoinCancel').addEventListener('click', () => { leaveOnline(); try { history.replaceState(null, '', location.pathname); } catch (_) {} showJoin(false); renderSetup(); });
    $('dtResume').addEventListener('click', () => { snd.unlock(); resumeMatch(); });
    $('dtDoubleIn').addEventListener('change', (e) => { setup.opts.x01.inRule = e.target.checked ? 'double' : 'straight'; saveSetup(); });
    // the statistics: a view of the same panel (on an ultrawide screen they have a column of their own)
    $('dtStatsBtn').addEventListener('click', () => selectSetupView('stats', true));
    $('dtStatsBack').addEventListener('click', () => selectSetupView('new', true));
}

function selectSetupView(k, byUser) {
    $('dtSetupNew').hidden = k !== 'new';
    $('dtStatsWrap').hidden = k !== 'stats';
    if (k === 'stats') renderAllTime($('dtStatsView'));
    if (byUser) {
        // focus follows a press only — never on load or on a layout change
        if (k === 'stats') feature('dt_stats_open');
        try { (k === 'stats' ? $('dtStatsBack') : $('dtStatsBtn')).focus({ preventScroll: true }); } catch (_) {}
    }
    fitSoon();
}
function renderSide() {
    // the third column: the match's numbers in a game, the all-time numbers before one
    if (app.layout !== 'xwide') return;
    if (root.dataset.state === 'setup') { renderAllTime($('dtSide')); $('dtStatsBtn').hidden = true; }
}

/* ═══════════════════════════ settings menu ═══════════════════════════ */
function bindMenu() {
    const sw = (id, key, after) => {
        const el = $(id);
        el.checked = !!settings[key];
        el.addEventListener('change', () => { settings[key] = el.checked; save('settings', settings); if (after) after(); });
    };
    sw('dtOptSound', 'sound', () => { snd.enabled = settings.sound; renderSoundBtn(); if (settings.sound) { snd.unlock(); snd.hit('single'); } });
    sw('dtOptCaller', 'caller', () => { snd.caller = settings.caller; if (settings.caller) snd.say(L('call_rest', { n: 501 })); feature('dt_caller', { on: settings.caller ? 1 : 0 }); });
    sw('dtOptHints', 'hints', () => { renderMatch(); });
    if (!snd.callerAvailable()) { $('dtOptCaller').disabled = true; $('dtCallerNa').hidden = false; }
    setSeg($('dtThrowStyle'), settings.throwStyle);
    seg($('dtThrowStyle'), (v) => { settings.throwStyle = v; save('settings', settings); root.dataset.throw = v; cancelAim(); if (app.s && isHumanTurn()) beginHumanDart(); renderActions(); feature('dt_throw_style', { style: v }); });
    root.dataset.throw = settings.throwStyle;
    radios($('dtThemes'), '.dt-theme', 'themeName', (v) => {
        const note = $('dtThemeNote');
        feature('dt_theme', { theme: v, locked: themeLocked(v) ? 1 : 0 });
        if (themeLocked(v)) {
            board.setTheme(v);
            markRadio($('dtThemes'), '.dt-theme', 'themeName', v);
            proNote(note, L('pro_theme_note'), 'theme');
            return;
        }
        note.hidden = true;
        settings.theme = v; save('settings', settings);
        applyTheme();
    });
    $('dtSoundBtn').addEventListener('click', () => {
        settings.sound = !settings.sound; save('settings', settings);
        snd.enabled = settings.sound; $('dtOptSound').checked = settings.sound; renderSoundBtn();
        if (settings.sound) { snd.unlock(); snd.hit('single'); }
    });
    $('dtFullBtn').addEventListener('click', toggleWhole);
    $('dtFullMenu').addEventListener('click', () => { closeMenu(); toggleWhole(); });
    $('dtRulesBtn').addEventListener('click', () => { closeMenu(); showRules(); });
    $('dtQuitBtn').addEventListener('click', () => { closeMenu(); quitMatch(); });
    applyTheme();
    renderSoundBtn();
    // who has Premium is known after the session bootstrap: a Premium board comes back then
    const again = () => applyTheme();
    document.addEventListener('tier-bootstrap:ready', again);
    window.addEventListener('auth-ready', again);
}
function themeLocked(th) { return (PRO.themes || []).indexOf(th) >= 0 && proLocked(); }
function applyTheme() {
    let th = settings.theme;
    if (themeLocked(th)) th = 'classic';
    board.setTheme(th);
    markRadio($('dtThemes'), '.dt-theme', 'themeName', th);
}
function renderSoundBtn() { $('dtSoundBtn').setAttribute('aria-pressed', settings.sound ? 'true' : 'false'); }
function closeMenu() {
    const m = $('dtMenu');
    try { if (window.__anchoredPopover) window.__anchoredPopover.close(m); else if (m.hidePopover) m.hidePopover(); } catch (_) {}
}

let whole = null;
function labelWhole() {
    const on = root.classList.contains('tmb-whole') || document.fullscreenElement === root;
    const t = on ? L('fullscreen_exit') : L('fullscreen');
    $('dtFullBtn').setAttribute('aria-label', t);
    $('dtFullBtn').title = t;
    $('dtFullMenu').textContent = t;
}
document.addEventListener('fullscreenchange', () => { labelWhole(); fit(true); });
function toggleWhole() {
    try {
        if (!whole) {
            const mk = window.GameKit && window.GameKit.immersive ? window.GameKit.immersive : null;
            whole = mk ? mk(root, { key: 'darts', onChange: () => fit(true) })
                : (window.__wholeScreen ? window.__wholeScreen(root, { key: 'darts', onChange: () => fit(true) }) : null);
        }
        if (whole) { whole.toggle(); feature('dt_fullscreen'); setTimeout(labelWhole, 300); }
    } catch (e) { if (window.__handleError) window.__handleError(e, 'darts_fullscreen'); }
}

/* ═══════════════════════════ layout: one screen ═══════════════════════════ */
let probe = null;
function viewH() {
    if (!probe) {
        probe = document.createElement('div');
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:fixed;left:0;top:0;width:0;height:100svh;visibility:hidden;pointer-events:none';
        document.body.appendChild(probe);
    }
    const h = probe.offsetHeight;
    return h > 100 ? Math.min(h, innerHeight) : innerHeight;
}
function fixedTop() {
    // the site's fixed bands at the top (the promo strip, the phone header): measured on the
    // elements themselves — a band with pointer-events:none or a gap between two bands hides
    // the next one from elementFromPoint. Kept even while the header is compacted: it can
    // come back, and the board must not hide under it.
    const vw = innerWidth, vhh = innerHeight;
    const cands = new Set([...document.body.children, ...document.querySelectorAll('header, [class*="banner"]')]);
    const bands = [];
    cands.forEach((e) => {
        if (e === root || e.contains(root)) return;
        // a prompt floating at the top (the language suggestion, a dialog) is not a band of the page
        if (e.id === 'locale-banner' || e.getAttribute('role') === 'dialog' || e.getAttribute('role') === 'alertdialog') return;
        const cs = getComputedStyle(e);
        if ((cs.position !== 'fixed' && cs.position !== 'sticky') || cs.display === 'none' || cs.visibility === 'hidden') return;
        const r = e.getBoundingClientRect();
        if (r.height <= 0 || r.height > vhh * 0.4 || r.width < vw * 0.5 || r.bottom <= 0 || r.top > vhh * 0.3) return;
        bands.push([r.top, r.bottom]);
    });
    bands.sort((x, y) => x[0] - y[0]);
    let y = 0;
    for (const [t, bt] of bands) if (t <= y + 32) y = Math.max(y, bt);
    return y;
}

let wwApi = null;
// a board: the whole band beside the sidebar, the columns centred in it (fill)
const wwOpts = { max: 3600, minGain: 120, minViewport: 1180, fill: true, onChange: () => fit(true) };
function ensureWide() {
    if (wwApi || !window.WideWorkspace) return;
    wwApi = window.WideWorkspace.attach(root, wwOpts);
}

let fitKey = '';
function fit(force) {
    const fs = root.classList.contains('tmb-whole');
    const W = root.clientWidth;
    if (!W) return;
    const vh = fs ? innerHeight : viewH();
    const game = root.dataset.state === 'game';
    const key = [W, Math.round(vh), game ? 1 : 0, root.dataset.keypad || '', fs ? 1 : 0, app.s ? app.s.players.length : 0, setup.mode].join('|');
    if (!force && key === fitKey) return;
    fitKey = key;
    const top = fs ? 0 : fixedTop();
    const avail = Math.max(320, vh - top - (fs ? 24 : 18));
    root.dataset.fitTop = String(Math.round(top));
    root.dataset.fitAvail = String(Math.round(avail));
    const gap = 14, statusH = 30;
    let layout, bw, rail = 0, side = 0;
    // the wide band: how wide this console would like to be on this screen
    const ideal = Math.min(avail - statusH - 8, 1280);
    wwOpts.max = Math.round(ideal + 520 + 420 + gap * 2);
    if (W >= 760) {
        rail = Math.round(Math.max(320, Math.min(420, W * 0.3)));
        bw = Math.min(avail - statusH - 8, W - rail - gap, 1280);
        layout = 'wide';
        const rem = W - bw - rail - gap * 2;
        if (rem >= 300) { layout = 'xwide'; side = Math.round(Math.min(520, rem)); }
    } else if (avail < 540 && W >= avail * 1.2 && W - 220 - gap >= 220) {
        // a phone on its side: the board the full height, the rail beside it
        layout = 'short';
        rail = Math.round(Math.max(220, Math.min(340, W - (avail - 8) - gap)));
        bw = Math.min(avail - statusH - 8, W - rail - gap);
    } else {
        layout = 'tall';
        bw = W;
        if (game) {
            const h = (id) => { const el = $(id); return el && !el.hidden && el.offsetParent !== null ? el.offsetHeight : 0; };
            const chrome = h('dtOnline') + h('dtPlayers') + h('dtVisit') + h('dtInput') + h('dtActions') + statusH + gap * (h('dtOnline') ? 5 : 4);
            bw = Math.min(W, avail - chrome);
            bw = Math.max(bw, Math.min(W, 240));
        }
    }
    bw = Math.max(200, Math.floor(bw));
    app.layout = layout;
    // a short screen gets the compact set-up (games behind one button, no description). The
    // full set-up of 501 measures 756 px on a 390 px phone — a 390×844 with the promo strip and
    // the header leaves 705, and the old «< 700» kept chips there and ran 41 px past the screen
    // (prod sweep 04.11.2026). A landscape phone (short) is always compact.
    const tight = !game && (layout === 'short' || (layout === 'tall' && avail < 770) || (layout !== 'tall' && avail < 800)) ? '1' : '';
    if (tight) root.dataset.tight = tight; else delete root.dataset.tight;
    if (root.dataset.layout !== layout) {
        root.dataset.layout = layout;
        placeTabs(layout);
        if (layout !== 'xwide') $('dtStatsBtn').hidden = false;
        if (layout === 'xwide' && root.dataset.state === 'setup') { selectSetupView('new'); }
        renderSide();
    }
    if (bw < 480) root.dataset.smallboard = '1'; else delete root.dataset.smallboard;
    placeModes(!!tight);
    root.style.setProperty('--dt-board', bw + 'px');
    root.style.setProperty('--dt-rail', rail + 'px');
    root.style.setProperty('--dt-side', side + 'px');
    board.resize(bw);
    requestAnimationFrame(() => {
        const st = $('dtStage');
        if (layout !== 'tall' && st) root.style.setProperty('--dt-stage-h', st.offsetHeight + 'px');
        else root.style.removeProperty('--dt-stage-h');
        if (!root.hasAttribute('data-fit')) root.setAttribute('data-fit', '1');
    });
    app.layout = layout;
}
let fitRaf = 0;
function fitSoon() { if (fitRaf) return; fitRaf = requestAnimationFrame(() => { fitRaf = 0; fit(true); }); }

function placeModes(tight) {
    // a short screen: the ten games behind one button (a menu), not four rows of chips
    const modes = $('dtModes'), btn = $('dtModeBtn'), pop = $('dtModePop');
    if (tight && modes.parentElement !== pop) { pop.appendChild(modes); btn.hidden = false; }
    else if (!tight && modes.parentElement === pop) { $('dtDesc').before(modes); btn.hidden = true; try { pop.hidePopover && pop.hidePopover(); } catch (_) {} }
    $('dtModeBtnName').textContent = L('mode_' + setup.mode);
    $('dtModeBtnCap').textContent = L('cap_' + setup.mode);
}
function placeTabs(layout) {
    // xwide: the match's statistics and log take the third column — both at once, no tabs;
    // elsewhere they sit in the rail behind two tabs
    const tb = $('dtTabsBlock');
    const inGame = root.dataset.state === 'game';
    const both = layout === 'xwide' && inGame;
    $('dtMTabs').hidden = both;
    if (both) { $('dtMStats').hidden = false; $('dtLogPanel').hidden = false; }
    else { const logOn = $('dtMTabLog').getAttribute('aria-selected') === 'true'; $('dtMStats').hidden = logOn; $('dtLogPanel').hidden = !logOn; }
    if (layout === 'xwide' && inGame) { if (tb.parentElement !== $('dtSide')) { $('dtSide').innerHTML = ''; $('dtSide').appendChild(tb); } }
    else if (tb.parentElement !== $('dtMatch')) $('dtMatch').appendChild(tb);
}

/* ═══════════════════════════ a match ═══════════════════════════ */
const isHuman = (i) => app.s && app.s.players[i].kind === 'human';
// online: only the seat of this device throws, and only while the other side hears it
const netReady = () => !!(app.net && app.net.connected && app.netState === 'on');
const isHumanTurn = () => app.s && !app.s.over && isHuman(app.s.cur) && (!app.netRole || (app.s.cur === app.me && netReady()));
const meIdx = () => (app.netRole ? app.me : 0);
const real = () => app.cfg && app.cfg.where === 'real';
const colorOf = (i) => PLAYER_COLORS[i % PLAYER_COLORS.length];

function buildCfg() {
    const m = setup.mode;
    const o = Object.assign({}, setup.opts[m] || {});
    const players = [];
    const solo = soloOnly(m) || setup.opp === 'solo';
    if (setup.opp === 'online' && !solo) {
        players.push({ name: (setup.myName || '').trim() || L('player_n', { n: 1 }), kind: 'human' });
        players.push({ name: L('online_opponent'), kind: 'human' });
    } else if (setup.opp === 'friends' && !solo) {
        for (let i = 0; i < setup.count; i++) players.push({ name: setup.names[i] || L('player_n', { n: i + 1 }), kind: 'human' });
    } else {
        players.push({ name: L('you'), kind: 'human' });
        if (!solo) {
            const lvl = setup.level === 'custom' && !proLocked() ? setup.custom : (setup.level === 'custom' ? 60 : levelAvg(setup.level));
            const lname = setup.level === 'custom' ? String(lvl) : L('level_' + setup.level);
            players.push({ name: L('bot_name', { n: lname }), kind: 'bot', level: lvl });
        }
    }
    if (m === 'cricket') o.cut = !!o.cut;
    if (m === 'checkouts') {
        o.count = 10; o.visits = 3;
        o.daily = o.drill === 'daily';
        o.seed = o.daily ? dailySeed() : ((Math.random() * 1e9) | 0) + 1;
    }
    return { mode: m, opts: o, players, where: setup.where, hand: setup.hand, at: Date.now() };
}
// the player's own calendar day (not Greenwich): the daily challenge turns at their midnight
function localYmd() {
    const d = new Date();
    if (window.__localYmd) return window.__localYmd(d);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}
function dailyKey() { try { return window.GameKit ? window.GameKit.dailyKey() : localYmd(); } catch (_) { return localYmd(); } }
function dailySeed() {
    try { if (window.GameKit) return window.GameKit.dailySeed(dailyKey(), 'darts-checkouts') || 1; } catch (_) {}
    let h = 7; for (const c of dailyKey()) h = (h * 31 + c.charCodeAt(0)) | 0; return Math.abs(h) || 1;
}

function startMatch(cfg) {
    clearTimeout(app.botTimer);
    app.cfg = cfg || buildCfg();
    app.events = [];
    app.assisted = false;
    app.s = createMatch(app.cfg);
    app.startedAt = Date.now();
    app.firstThrow = false;
    app.typed = ''; app.ask = null; app.shown = null; app.finished = false; app.rq = []; app.remoteFlying = false;
    app.inMode = keypadKind(app.cfg.mode) && real() ? (keypadKind(app.cfg.mode) === 'hits' ? 'hits' : 'total') : 'darts';
    board.clearDarts();
    board.setHeat(null);
    heat.points = []; heat.off = [];
    enterGame();
    if (app.netRole === 'host') netSync();
    stage('game_start', { mode: app.cfg.mode, where: app.cfg.where, opp: oppKind(), n: app.cfg.players.length });
    feature('dt_start', { mode: app.cfg.mode, where: app.cfg.where, opp: oppKind(), level: botLevel() });
    persist();
    nextTurn(true);
}
function oppKind() { const c = app.cfg; if (!c) return ''; if (c.players.length === 1) return 'solo'; if (app.netRole) return 'online'; return c.players.some((p) => p.kind === 'bot') ? 'bot' : 'friends'; }
function botLevel() { const b = app.cfg && app.cfg.players.find((p) => p.kind === 'bot'); return b ? b.level : 0; }

function enterGame() {
    const go = () => {
        root.dataset.state = 'game';
        root.dataset.input = app.cfg.where;
        $('dtSetup').hidden = true;
        $('dtMatch').hidden = false;
        $('dtQuitBtn').hidden = false;
        board.setHighlight(null);
        $('dtStatsWrap').hidden = true; $('dtSetupNew').hidden = false;
        placeTabs(app.layout);
        renderInput();
        renderMatch();
        fit(true);
        requestAnimationFrame(() => requestAnimationFrame(bringMatchIntoView));
    };
    if (window.__viewTransition) window.__viewTransition(go); else go();
    if (real() && window.__wakeLock) { try { window.__wakeLock.request(); } catch (_) {} }
}
/* The match's core (players, board, visit, buttons) on screen after the set-up gave way:
   «Play» sits low in the set-up, and the board it opens could start above the window.
   Only scrollIntoView({block:'nearest'}) — no scroll when it is already visible — and the
   site's fixed bands are kept out by scroll-margin-top. */
function bringMatchIntoView() {
    const top = fixedTop() + 8;
    const first = app.layout === 'tall' ? $('dtPlayers') : $('dtStage');
    const last = app.layout === 'tall' ? (root.dataset.keypad ? $('dtInput') : $('dtActions')) : $('dtStage');
    if (!first || !last) return;
    first.style.scrollMarginTop = top + 'px';
    last.style.scrollMarginBottom = '8px';
    const a = first.getBoundingClientRect(), b = last.getBoundingClientRect();
    try {
        if (a.top < top) first.scrollIntoView({ block: 'nearest' });
        else if (b.bottom > innerHeight) last.scrollIntoView({ block: 'nearest' });
    } catch (_) {}
}
function leaveGame() {
    const go = () => {
        root.dataset.state = 'setup';
        delete root.dataset.keypad;
        $('dtSetup').hidden = false;
        $('dtMatch').hidden = true;
        $('dtQuitBtn').hidden = true;
        cancelAim();
        board.clearDarts();
        board.setHeat(null);
        $('dtStatus').textContent = '';
        placeTabs(app.layout);
        renderSetup();
        fit(true);
        renderSide();
    };
    if (window.__viewTransition) window.__viewTransition(go); else go();
    try { window.__wakeLock && window.__wakeLock.release(); } catch (_) {}
}
function quitMatch() {
    clearTimeout(app.botTimer);
    const unfinished = app.s && !app.s.over && app.events.length;
    if (app.s && !app.s.over) {
        stage('abandoned', { mode: app.cfg.mode, darts: app.events.length });
        feature('dt_quit', { mode: app.cfg.mode, events: app.events.length, online: app.netRole ? 1 : 0 });
    }
    if (app.netRole) {
        // a match by link cannot go on alone: the other side is told, the room closes
        leaveOnline();
        clearSave();
    } else {
        persist();
        if (unfinished) { try { window.__toast && window.__toast(L('quit_confirm'), 'info'); } catch (_) {} }
    }
    app.s = null;
    leaveGame();
}

/* ── events: every change of the score goes through here ── */
function commit(ev, remote) {
    if (!app.s || app.s.over) return null;
    const before = app.s.cur;
    const visitWasEmpty = app.s.visit.length === 0;
    const res = apply(app.s, ev);
    app.events.push(ev);
    if (app.netRole && !remote && app.net) app.net.send({ t: 'ev', i: app.events.length - 1, ev });
    if (!app.firstThrow) { app.firstThrow = true; stage('first_throw', { mode: app.cfg.mode }); }
    react(res, before, visitWasEmpty);
    persist();
    renderMatch();
    if (app.s.over) { finishMatch(); return res; }
    if (res.visitDone) {
        // the visit is over: a breath, then the next player
        app.phase = 'between';
        cancelAim();
        clearTimeout(app.botTimer);
        app.botTimer = setTimeout(() => nextTurn(false), res.legWon ? 1500 : (real() ? 250 : 800));
    } else {
        continueVisit();
    }
    return res;
}

function react(res, pid, firstOfVisit) {
    const d = res.dart;
    const s = app.s;
    if (d) {
        const label = d.s ? cl(d.s, d.m) : L('miss');
        try { window.__announce && window.__announce(label); } catch (_) {}
    }
    if (res.bust) { call(L('bust'), 'bad'); snd.bust(); snd.say(L('call_bust')); try { navigator.vibrate && navigator.vibrate([30, 40, 30]); } catch (_) {} }
    else if (res.shanghai) { call(L('shanghai'), 'hot'); snd.cheer(true); }
    else if (res.legWon) {
        const last = s.over;
        call(app.cfg.mode === 'x01' ? L('game_shot') : L('leg'), 'hot');
        snd.cheer(last); snd.say(last ? L('call_match') : L('call_leg'));
    } else if (res.visitDone && s.last) {
        const t = s.last.total;
        if (t === 180) { call(L('one80'), 'hot'); snd.cheer(true); }
        if (app.cfg.mode === 'x01') {
            // like a referee: the score, then «N left» when the thrower is on a finish
            const pl = s.players[s.last.pid];
            const fin = !s.last.bust && pl && pl.rest > 0 && route(pl.rest, 3, app.cfg.opts.outRule);
            snd.say(fin ? t + '. ' + L('call_rest', { n: pl.rest }) : String(t));
        } else if (roundsOf(app.cfg)) snd.say(String(t));
    }
    app.shown = null;
}

function call(text, kind) {
    const el = $('dtCall');
    el.textContent = text;
    el.className = 'dt-call' + (kind ? ' ' + kind : '');
    void el.offsetWidth;
    el.classList.add('go');
    const st = $('dtStatus');
    if (root.dataset.keypad) { try { window.__toast && window.__toast(text, kind === 'bad' ? 'warning' : 'success'); } catch (_) {} }
    st.dataset.flash = text;
}

function nextTurn(first) {
    clearTimeout(app.botTimer);
    if (!app.s || app.s.over) return;
    renderMatch();
    if (app.netRole && !isHumanTurn()) { app.phase = 'remote'; cancelAim(); renderStatus(); renderActions(); drainRemote(); return; }
    if (!isHumanTurn()) { app.phase = 'bot'; renderStatus(); app.botTimer = setTimeout(botDart, first ? 900 : 700); return; }
    if (real()) { app.phase = 'input'; renderStatus(); return; }
    beginHumanDart();
}
function continueVisit() {
    if (!app.s || app.s.over) return;
    if (app.netRole && !isHumanTurn()) { app.phase = 'remote'; renderStatus(); drainRemote(); return; }
    if (!isHumanTurn()) { app.phase = 'bot'; app.botTimer = setTimeout(botDart, 560); renderStatus(); return; }
    if (real()) { app.phase = 'input'; renderStatus(); return; }
    beginHumanDart();
}

/* ── the bot ── */
function botDart() {
    if (!app.s || app.s.over || isHumanTurn() || app.netRole) return;
    const p = app.s.players[app.s.cur];
    const ev = throwDart(app.s, sigmaFor(p.level || 60));
    delete ev.aim;
    flyDart(ev, app.s.cur);
}

/* ── a dart in the air: the score changes when it sticks ── */
function flyDart(ev, pid, remote) {
    app.phase = 'flight';
    if (app.s.visit.length === 0 && app.cfg.mode !== 'free') board.clearDarts();
    if (app.cfg.mode === 'free' && app.s.visit.length === 0) board.clearDarts();
    const hot = ev.m === 3 || (ev.s === 25 && ev.m === 2);
    board.throwDart({
        x: ev.x, y: ev.y, color: colorOf(pid), hot, miss: !ev.s,
        label: ev.s ? cl(ev.s, ev.m) : '0',
        done: () => {
            snd.hit(!ev.s ? 'miss' : (hot ? 'hot' : 'single'));
            try { if (navigator.vibrate && ev.s && isHuman(pid)) navigator.vibrate(hot ? [12, 24, 12] : 14); } catch (_) {}
            if (app.cfg.mode === 'free') addHeat(ev);
            if (remote) app.remoteFlying = false;
            commit(ev, remote);
            if (remote) drainRemote();
        },
    });
}

/* ═══════════════════════════ the throw (screen board) ═══════════════════════════ */
const aim = { on: false, holding: false, touch: false, x: 0, y: -0.6, t: 0, hold: 0, wx: 0, wy: 0, view: null, sweep: null, keys: false };

function beginHumanDart() {
    app.phase = 'aim';
    renderStatus();
    renderActions();
    if (settings.throwStyle === 'sweep') { startSweep(); return; }
    // keyboard players keep their aim; the mouse shows it on hover
    if (aim.keys) showAim();
}
function cancelAim() {
    aim.on = false; aim.holding = false; aim.sweep = null;
    board.setAim(null); board.setSweep(null); board.setLoupe(null); board.setHover(null);
}
function hand() { return HAND[app.cfg && app.cfg.hand] || HAND.mid; }

function sway() {
    // the hand: two slow sines and a random walk; holding steadies it, then it tires
    const h = hand();
    const tt = aim.t / 1000;
    const hs = aim.hold / 1000;
    let f = 1;
    if (aim.holding) {
        if (hs < 0.9) f = 1 - 0.62 * (hs / 0.9);
        else if (hs < 2.3) f = 0.38;
        else f = Math.min(1.5, 0.38 + (hs - 2.3) * 0.45);
    }
    const A = h.amp * f;
    const x = A * (0.62 * Math.sin(tt * 1.9) + 0.38 * Math.sin(tt * 1.05 + 1.3)) + aim.wx * f;
    const y = A * (0.62 * Math.sin(tt * 1.45 + 0.5) + 0.38 * Math.sin(tt * 2.3)) + aim.wy * f;
    const calm = Math.max(0, Math.min(1, (1.5 - f) / (1.5 - 0.38)));
    return { x, y, calm };
}

const frameHook = (dt) => {
    if (aim.on || aim.sweep) {
        aim.t += dt;
        if (aim.holding) aim.hold += dt;
        const A = hand().amp;
        aim.wx = Math.max(-A * 0.6, Math.min(A * 0.6, aim.wx + (Math.random() - 0.5) * A * 0.06));
        aim.wy = Math.max(-A * 0.6, Math.min(A * 0.6, aim.wy + (Math.random() - 0.5) * A * 0.06));
    }
    if (aim.on && aim.view) {
        const w = sway();
        aim.view.x = aim.x + w.x; aim.view.y = aim.y + w.y; aim.view.calm = w.calm; aim.view.holding = aim.holding;
        if (aim.touch && aim.loupe) { aim.loupe.x = aim.view.x; aim.loupe.y = aim.view.y; }
    }
    if (aim.sweep) {
        const sw = aim.sweep, per = hand().sweep;
        const tri = (c) => { const fr = (c % per) / per; const t = fr < 0.5 ? fr * 2 : 2 - fr * 2; return -1.12 + (t * t * (3 - 2 * t)) * 2.24; };
        if (sw.phase === 'x') sw.x = tri(aim.t);
        else if (sw.phase === 'y') sw.y = tri(aim.t);
        else if (sw.phase === 'go' && aim.view) {
            const w = sway();
            aim.view.x = sw.x + w.x; aim.view.y = sw.y + w.y; aim.view.calm = w.calm;
        }
    }
};
frameHook.active = () => aim.on || !!aim.sweep;
board.onFrame(frameHook);

function showAim() {
    if (!aim.view) aim.view = { x: aim.x, y: aim.y, calm: 0.5, holding: false };
    aim.on = true;
    board.setAim(aim.view);
}

function release() {
    if (app.phase !== 'aim' || !isHumanTurn()) return;
    if (!app.taught) { app.taught = true; save('taught', 1); }
    const h = hand();
    const w = sway();
    const ax = aim.x, ay = aim.y;
    const x = ax + w.x + gauss(Math.random) * h.res;
    const y = ay + w.y + gauss(Math.random) * h.res;
    const hit = hitAt(x, y);
    aim.holding = false; aim.hold = 0;
    board.setLoupe(null);
    if (!aim.keys && aim.touch) { aim.on = false; board.setAim(null); }
    const ev = { k: 'd', s: hit.s, m: hit.m, x: +x.toFixed(4), y: +y.toFixed(4), ax: +ax.toFixed(4), ay: +ay.toFixed(4) };
    flyDart(ev, app.s.cur);
}

function startSweep() {
    aim.sweep = { phase: 'x', x: 0, y: null };
    aim.t = 0;
    board.setSweep(aim.sweep);
    board.setAim(null);
    renderStatus();
}
function sweepPress() {
    if (app.phase !== 'aim' || !isHumanTurn() || !aim.sweep) return;
    const sw = aim.sweep;
    snd.unlock();
    if (sw.phase === 'x') { sw.phase = 'y'; aim.t = 0; sw.y = 0; }
    else if (sw.phase === 'y') {
        sw.phase = 'go'; aim.t = 0; aim.hold = 0; aim.holding = true;
        aim.view = { x: sw.x, y: sw.y, calm: 0.5, holding: true };
        board.setAim(aim.view);
    } else {
        aim.x = sw.x; aim.y = sw.y;
        aim.sweep = null; board.setSweep(null);
        aim.on = true;
        release();
        aim.on = false; board.setAim(null);
        return;
    }
    renderStatus();
}

/* pointer on the board */
function bindBoard() {
    const cv = $('dtCanvas');
    let pid = null, downAt = 0;
    cv.addEventListener('pointerdown', (e) => {
        if (!app.s || app.s.over) return;
        snd.unlock();
        if (real()) { realDown(e); return; }
        if (settings.throwStyle === 'sweep') { e.preventDefault(); sweepPress(); return; }
        if (app.phase !== 'aim' || !isHumanTurn()) return;
        e.preventDefault();
        pid = e.pointerId; downAt = performance.now();
        try { cv.setPointerCapture(pid); } catch (_) {}
        aim.keys = false;
        aim.touch = e.pointerType !== 'mouse';
        const p = board.toBoard(e.clientX, e.clientY);
        aim.x = p.x; aim.y = p.y;
        aim.holding = true; aim.hold = 0;
        showAim();
        if (aim.touch) { aim.loupe = { x: p.x, y: p.y, color: '#fde047' }; board.setLoupe(aim.loupe); }
    });
    cv.addEventListener('pointermove', (e) => {
        if (!app.s || app.s.over) return;
        if (real()) { realMove(e); return; }
        if (settings.throwStyle === 'sweep' || app.phase !== 'aim' || !isHumanTurn()) return;
        const list = e.getCoalescedEvents ? e.getCoalescedEvents() : null;
        const last = list && list.length ? list[list.length - 1] : e;
        const p = board.toBoard(last.clientX, last.clientY);
        aim.x = p.x; aim.y = p.y;
        if (e.pointerType === 'mouse' && !aim.on) { aim.touch = false; aim.keys = false; showAim(); }
    });
    const up = (e, cancel) => {
        if (real()) { realUp(e, cancel); return; }
        if (pid === null || e.pointerId !== pid) return;
        pid = null;
        try { cv.releasePointerCapture(e.pointerId); } catch (_) {}
        if (cancel) { aim.holding = false; board.setLoupe(null); if (aim.touch) { aim.on = false; board.setAim(null); } return; }
        // let go outside the board's square: no throw (a way out of a press)
        const r = cv.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
            aim.holding = false; board.setLoupe(null); if (aim.touch) { aim.on = false; board.setAim(null); }
            return;
        }
        if (performance.now() - downAt < 40 && aim.touch) { /* a graze, not a throw */ }
        release();
    };
    cv.addEventListener('pointerup', (e) => up(e, false));
    cv.addEventListener('pointercancel', (e) => up(e, true));
    cv.addEventListener('pointerleave', (e) => {
        if (real()) { board.setHover(null); return; }
        if (e.pointerType === 'mouse' && pid === null && !aim.keys) { aim.on = false; board.setAim(null); }
    });
    cv.addEventListener('contextmenu', (e) => e.preventDefault());
}

/* keyboard: arrows aim, Space/Enter hold and release; U or Ctrl+Z undo */
function bindKeys() {
    document.addEventListener('keydown', (e) => {
        if (!app.s || root.dataset.state !== 'game') return;
        if (document.querySelector('.dt-dlg[open]')) return;
        const owned = window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : /^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test((e.target && e.target.tagName) || '');
        // the letter by its place on the keyboard: Ctrl+Z is «я» on the Russian layout
        const letter = window.__keyLetter ? window.__keyLetter(e) : (/^Key[A-Z]$/.test(e.code || '') ? e.code.slice(3).toLowerCase() : '');
        if (!owned && (((e.ctrlKey || e.metaKey) && letter === 'z') || (!e.ctrlKey && !e.metaKey && !e.altKey && letter === 'u'))) {
            e.preventDefault(); undo(); return;
        }
        if (real()) { keypadKey(e, owned); return; }
        if (owned && e.target !== $('dtCanvas')) return;
        if (settings.throwStyle === 'sweep') {
            if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); sweepPress(); }
            return;
        }
        if (app.phase !== 'aim' || !isHumanTurn()) return;
        const step = e.shiftKey ? 0.08 : 0.025;
        const mv = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
        if (mv) {
            e.preventDefault();
            aim.keys = true; aim.touch = false;
            aim.x = Math.max(-1.15, Math.min(1.15, aim.x + mv[0]));
            aim.y = Math.max(-1.15, Math.min(1.15, aim.y + mv[1]));
            showAim();
            return;
        }
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            snd.unlock();
            aim.keys = true; aim.touch = false;
            showAim();
            aim.holding = true; aim.hold = 0;
        }
        if (e.key === 'Escape') { aim.holding = false; }
    });
    document.addEventListener('keyup', (e) => {
        if (!app.s || root.dataset.state !== 'game' || real() || settings.throwStyle === 'sweep') return;
        if ((e.key === ' ' || e.key === 'Enter') && aim.holding && aim.keys) { e.preventDefault(); release(); }
    });
}

/* ═══════════════════════════ a real board: the scorer ═══════════════════════════ */
const QUICK = [26, 41, 45, 60, 81, 85, 100, 140, 180];

function renderInput() {
    const on = real() && app.s && !app.s.over;
    $('dtInput').hidden = !on;
    if (!on) { delete root.dataset.keypad; return; }
    const kind = keypadKind(app.cfg.mode);
    const canTotal = kind === 'total';
    $('dtInMode').hidden = !canTotal;
    if (!canTotal && app.inMode === 'total') app.inMode = kind === 'hits' ? 'hits' : 'darts';
    setSeg($('dtInMode'), app.inMode, 'aria-selected');
    $('dtKeypad').hidden = app.inMode !== 'total';
    $('dtDartBar').hidden = app.inMode !== 'darts';
    $('dtHits').hidden = app.inMode !== 'hits';
    if (app.inMode === 'total' || app.inMode === 'hits') root.dataset.keypad = '1'; else delete root.dataset.keypad;
    $('dtCanvas').setAttribute('aria-label', app.inMode === 'darts' ? L('aria_board_real') : L('aria_board'));
    renderKeypad();
}
function renderKeypad() {
    $('dtKOut').textContent = app.typed || '0';
    const p = app.s && app.s.players[app.s.cur];
    if (!p) return;
    $('dtKHint').textContent = L('real_hint_total', { name: p.name });
    const q = $('dtQuick');
    const items = QUICK.slice();
    let html = '';
    if (app.cfg.mode === 'x01' && route(p.rest, 3, app.cfg.opts.outRule)) html += `<button type="button" class="dt-q" data-q="${p.rest}" style="border-color:var(--lg-success)">${p.rest} ✓</button>`;
    html += items.map((v) => `<button type="button" class="dt-q" data-q="${v}">${v}</button>`).join('');
    q.innerHTML = html;
}
function keyPress(k) {
    if (app.ask) return;
    if (k === 'c') { app.typed = app.typed.slice(0, -1); }
    else if (k === 'ok') { submitTotal(app.typed === '' ? 0 : +app.typed); return; }
    else if (app.typed.length < 3) {
        const nv = (app.typed + k).replace(/^0+(?=\d)/, '');
        if (+nv <= 180) app.typed = nv;
    }
    $('dtKDisp').classList.remove('err');
    renderKeypad();
}
function keypadKey(e, owned) {
    if (owned && e.target && e.target.tagName === 'INPUT') return;
    const n = digitOf(e);
    if (app.ask) {
        if (n !== null) { const b = $('dtAskSeg').querySelector(`[data-v="${n}"]`); if (b) { e.preventDefault(); b.click(); } }
        return;
    }
    if (app.inMode !== 'total') return;
    if (n !== null) { e.preventDefault(); keyPress(n); }
    else if (e.key === 'Backspace') { e.preventDefault(); keyPress('c'); }
    else if (e.key === 'Enter' && !owned) { e.preventDefault(); keyPress('ok'); }
}
/* a digit by its key's place (the number row or the numpad): AZERTY types «& é "» without Shift */
function digitOf(e) {
    const m = /^(?:Digit|Numpad)([0-9])$/.exec(e.code || '');
    return m && !e.ctrlKey && !e.metaKey && !e.altKey ? m[1] : null;
}
function flashErr(msg) {
    $('dtKDisp').classList.add('err');
    $('dtKHint').textContent = msg;
    try { navigator.vibrate && navigator.vibrate(40); } catch (_) {}
}
function submitTotal(pts) {
    if (!app.s || app.s.over || !isHumanTurn()) return;
    if (impossibleVisit(pts)) { flashErr(L('impossible', { n: pts })); return; }
    const p = app.s.players[app.s.cur];
    if (app.cfg.mode === 'x01') {
        const out = app.cfg.opts.outRule;
        if (pts === p.rest && p.opened) {
            const ns = [1, 2, 3].filter((n) => route(p.rest, n, out));
            if (!ns.length) { flashErr(L('no_checkout', { n: pts })); return; }
            askQ(L('checkout_q'), ns, (n) => {
                const dd = n === 1 || out === 'single' ? 1 : null;
                if (dd != null) { pushTotal({ k: 'v', pts, n, dd }); return; }
                askQ(L('double_q'), Array.from({ length: n }, (_, i) => i + 1), (d) => pushTotal({ k: 'v', pts, n, dd: d }));
            });
            return;
        }
        if (out !== 'single' && oneDartFinish(p.rest, out) && p.rest <= 50) {
            askQ(L('double_q'), [0, 1, 2, 3], (d) => pushTotal({ k: 'v', pts, dd: d }));
            return;
        }
    }
    pushTotal({ k: 'v', pts });
}
function pushTotal(ev) {
    app.typed = '';
    app.ask = null;
    $('dtAsk').hidden = true;
    commit(ev);
    renderInput();
}
function askQ(q, options, cb) {
    app.ask = { cb };
    $('dtAskQ').textContent = q;
    const sg = $('dtAskSeg');
    sg.innerHTML = options.map((n) => `<button type="button" class="tc-seg-b" role="radio" aria-checked="false" data-v="${n}">${n}</button>`).join('');
    $('dtAsk').hidden = false;
    fitSoon();
    const first = sg.querySelector('button');
    if (first) try { first.focus({ preventScroll: true }); } catch (_) {}
}
function bindKeypad() {
    $('dtKeys').addEventListener('click', (e) => { const b = e.target.closest('[data-k]'); if (b) keyPress(b.dataset.k); });
    $('dtQuick').addEventListener('click', (e) => { const b = e.target.closest('[data-q]'); if (b) submitTotal(+b.dataset.q); });
    $('dtHits').addEventListener('click', (e) => { const b = e.target.closest('[data-h]'); if (b && isHumanTurn()) { commit({ k: 'h', n: +b.dataset.h }); renderInput(); } });
    $('dtAskSeg').addEventListener('click', (e) => {
        const b = e.target.closest('[data-v]');
        if (!b || !app.ask) return;
        const cb = app.ask.cb;
        app.ask = null;
        $('dtAsk').hidden = true;
        cb(+b.dataset.v);
    });
    seg($('dtInMode'), (v) => { app.inMode = v; renderInput(); fit(true); feature('dt_input', { mode: v }); }, 'aria-selected');
    $('dtMissBtn').addEventListener('click', () => { if (real() && isHumanTurn()) placeRealDart({ s: 0, m: 0, x: null, y: null }); });
}

/* per dart: a touch on the board (the magnifier shows the bed under the finger) */
let realPid = null;
function realHit(e) { const p = board.toBoard(e.clientX, e.clientY); const h = hitAt(p.x, p.y); return { p, h }; }
function bedOf(h) { return h.s ? { s: h.s, m: h.s === 25 ? h.m : h.m, outer: true } : null; }
function realDown(e) {
    if (app.inMode !== 'darts' || !isHumanTurn() || app.ask) return;
    e.preventDefault();
    realPid = e.pointerId;
    try { $('dtCanvas').setPointerCapture(realPid); } catch (_) {}
    realMove(e, true);
}
function realMove(e, force) {
    if (app.inMode !== 'darts' || !isHumanTurn()) return;
    const { p, h } = realHit(e);
    board.setHover(h.s ? hoverBed(p, h) : null);
    if ((realPid !== null || force) && e.pointerType !== 'mouse') board.setLoupe({ x: p.x, y: p.y, label: h.s ? cl(h.s, h.m) : L('miss'), color: '#fff' });
}
function hoverBed(p, h) {
    if (h.s === 25) return { s: 25, m: h.m };
    const d = Math.hypot(p.x, p.y);
    return { s: h.s, m: h.m, outer: h.m === 1 && d > 0.62 };
}
function realUp(e, cancel) {
    if (realPid === null || e.pointerId !== realPid) return;
    realPid = null;
    board.setLoupe(null);
    if (cancel) return;
    const { p, h } = realHit(e);
    if (Math.hypot(p.x, p.y) > 1.2) { board.setHover(null); return; }
    placeRealDart({ s: h.s, m: h.m, x: p.x, y: p.y });
}
function placeRealDart(d) {
    if (app.s.visit.length === 0) board.clearDarts();
    const pid = app.s.cur;
    const pt = d.x != null ? { x: d.x, y: d.y } : null;
    if (pt) board.placeDart({ x: pt.x, y: pt.y, color: colorOf(pid) });
    snd.hit(!d.s ? 'miss' : (d.m === 3 || (d.s === 25 && d.m === 2) ? 'hot' : 'single'));
    try { navigator.vibrate && navigator.vibrate(12); } catch (_) {}
    const ev = { k: 'd', s: d.s, m: d.m };
    if (pt) { ev.x = +pt.x.toFixed(4); ev.y = +pt.y.toFixed(4); }
    commit(ev);
    renderInput();
}

/* ═══════════════════════════ online: a match by link ═══════════════════════════ */
/* Loaded on demand: a game against the computer never needs PeerJS or the room layer. */
let onlineMod = null;
function vendor(src, expect) {
    if (!src) return Promise.reject(new Error('no url'));
    if (window.__vendorScript) return window.__vendorScript(src, { expect });
    return new Promise((res, rej) => {
        const sc = document.createElement('script');
        sc.src = Array.isArray(src) ? src[0] : src;
        sc.onload = () => res(); sc.onerror = () => rej(new Error('load ' + sc.src));
        document.head.appendChild(sc);
    });
}
function loadOnlineDeps() {
    if (!onlineMod) {
        const N = CFG.net || {};
        onlineMod = vendor(N.peer, 'Peer')
            .then(() => vendor(N.webrtc, '__WebRTC'))
            .then(() => vendor(N.room, '__RoomConnect'))
            .then(() => import('./online.js'))
            .catch((e) => { onlineMod = null; throw e; });
    }
    return onlineMod;
}
function netUnavailable(err) {
    feature('dt_online_failed', { why: String(err && err.message || err).slice(0, 60) });
    try { window.__toast && window.__toast(L('online_error'), 'error'); } catch (_) {}
    app.netState = 'error';
    renderOnline(); renderStatus();
}
function netCallbacks() {
    return {
        onStatus: netStatus,
        onHello: (msg) => {
            // the guest is here: its name on its seat, then the whole match to it
            const nm = String(msg && msg.name || '').trim().slice(0, 16);
            if (app.cfg && app.cfg.players[1]) {
                app.cfg.players[1].name = nm || L('online_opponent');
                app.s = replay(app.cfg, app.events);
            }
            app.netState = 'on';
            netSync();
            persist();
            renderOnline();
            feature('dt_online_connected', { role: 'host' });
            if (app.s && app.s.over) renderMatch(); else nextTurn(false);
        },
        onSync: adoptSync,
        onSyncRequest: () => netSync(),
        onMessage: onNetMessage,
    };
}
function netSync() { if (app.net && app.cfg) app.net.send({ t: 'sync', cfg: app.cfg, events: app.events }); }
function requestSync() { if (app.netRole === 'host') netSync(); else if (app.net) app.net.send({ t: 'syncreq' }); }
function netStatus(k) {
    if (k === 'waiting') app.netState = app.netState === 'on' ? 'on' : 'waiting';
    else if (k === 'connected') { if (app.netRole === 'guest') app.netState = 'joining'; }
    else if (k === 'lost' || k === 'quiet' || k === 'rejoining' || k === 'reclaiming') { if (app.netState === 'on') app.netState = 'lost'; }
    else if (k === 'not_found' || k === 'full' || k === 'error' || k === 'unavailable') {
        const msg = k === 'not_found' ? 'online_not_found' : k === 'full' ? 'online_full' : 'online_error';
        try { window.__toast && window.__toast(L(msg), 'error'); } catch (_) {}
        feature('dt_online_' + k);
        if (app.netRole === 'guest' && !app.s) { leaveOnline(); showJoin(false); renderSetup(); return; }
        app.netState = 'error';
    }
    renderOnline();
    if (app.s) { renderStatus(); renderActions(); if (app.netState !== 'on') { cancelAim(); if (app.phase === 'aim' || app.phase === 'input') app.phase = 'remote'; } }
}
function adoptSync(cfg, events) {
    // the host's copy is the match: adopt it whole
    const fresh = !app.s || root.dataset.state !== 'game';
    clearTimeout(app.botTimer);
    cancelAim();
    const sameMatch = app.cfg && app.cfg.at === cfg.at;
    app.cfg = cfg;
    app.events = events.slice();
    app.s = replay(cfg, app.events);
    app.rq = []; app.remoteFlying = false;
    if (!sameMatch) { app.finished = false; app.startedAt = Date.now(); }
    app.inMode = keypadKind(cfg.mode) && real() ? (keypadKind(cfg.mode) === 'hits' ? 'hits' : 'total') : 'darts';
    try { const d = $('dtEnd'); if (d.open && !app.s.over) d.close(); } catch (_) {}
    app.netState = 'on';
    showJoin(false);
    if (fresh) enterGame(); else { renderInput(); renderMatch(); }
    rebuildBoardDarts();
    if (cfg.mode === 'free') rebuildHeat();
    persist();
    renderOnline();
    feature('dt_online_connected', { role: 'guest' });
    if (app.s.over) finishMatch(); else nextTurn(false);
}
function onNetMessage(msg) {
    if (!app.s || !msg) return;
    if (msg.t === 'ev') {
        const expect = app.events.length + (app.remoteFlying ? 1 : 0) + app.rq.length;
        if (msg.i < expect) return;                     // seen already (a reconnection)
        if (msg.i > expect || app.s.over) { requestSync(); return; }
        app.rq.push(msg.ev);
        drainRemote();
    } else if (msg.t === 'trunc') {
        const n = msg.n | 0;
        if (!app.remoteFlying && !app.rq.length && n < app.events.length && n >= app.events.length - 3) applyTrunc(n);
        else requestSync();
    } else if (msg.t === 'again') {
        if (app.netRole === 'host' && app.s.over) rematch();
    } else if (msg.t === 'bye') {
        try { window.__toast && window.__toast(L('online_left'), 'info'); } catch (_) {}
        app.netState = 'lost';
        renderOnline(); renderStatus();
    }
}
/* the other side's events, one at a time: a dart still in the air holds the next back */
function drainRemote() {
    while (!app.remoteFlying && app.rq.length && app.s && !app.s.over && app.phase !== 'flight') {
        if (app.s.cur === app.me) { app.rq = []; requestSync(); return; }  // it is our turn — the copies disagree
        const ev = app.rq.shift();
        if (ev.k === 'd' && app.cfg.where === 'screen' && ev.x != null) { app.remoteFlying = true; flyDart(ev, app.s.cur, true); return; }
        if (ev.k === 'd' && ev.x != null) {
            if (app.s.visit.length === 0) board.clearDarts();
            board.placeDart({ x: ev.x, y: ev.y, color: colorOf(app.s.cur) });
        }
        if (ev.k === 'd') snd.hit(!ev.s ? 'miss' : (ev.m === 3 || (ev.s === 25 && ev.m === 2) ? 'hot' : 'single'));
        commit(ev, true);
        renderInput();
    }
}
function applyTrunc(n) {
    clearTimeout(app.botTimer);
    cancelAim();
    app.events = app.events.slice(0, n);
    app.s = replay(app.cfg, app.events);
    rebuildBoardDarts();
    if (app.cfg.mode === 'free') rebuildHeat();
    persist();
    renderInput();
    nextTurn(false);
}
function rematch() {
    const cfg = Object.assign({}, app.cfg, { at: Date.now() });
    if (cfg.mode === 'checkouts' && !cfg.opts.daily) cfg.opts = Object.assign({}, cfg.opts, { seed: ((Math.random() * 1e9) | 0) + 1 });
    try { const d = $('dtEnd'); if (d.open) d.close(); } catch (_) {}
    feature('dt_again', { mode: cfg.mode, online: 1 });
    startMatch(cfg);
}
function inviteUrl() { return location.origin + location.pathname + '?room=' + encodeURIComponent(app.netCode || ''); }
function startOnlineHost(resume) {
    leaveOnline(true);
    app.netRole = 'host'; app.me = 0; app.netState = 'waiting';
    root.dataset.online = '1';
    if (resume) {
        app.cfg = resume.cfg; app.events = resume.events.slice(); app.s = replay(app.cfg, app.events);
        app.startedAt = Date.now(); app.firstThrow = true; app.finished = false; app.rq = []; app.remoteFlying = false;
        app.inMode = keypadKind(app.cfg.mode) && real() ? (keypadKind(app.cfg.mode) === 'hits' ? 'hits' : 'total') : 'darts';
        enterGame();
        rebuildBoardDarts();
        nextTurn(false);
    } else {
        startMatch(buildCfg());
    }
    feature('dt_online_host', { mode: app.cfg.mode, resume: resume ? 1 : 0 });
    renderOnline();
    loadOnlineDeps().then((O) => {
        if (app.netRole !== 'host' || app.net) return;
        app.net = O.create(netCallbacks());
        app.netCode = app.net.host(resume ? resume.code : undefined);
        try { history.replaceState(null, '', location.pathname + '?host=' + encodeURIComponent(app.netCode)); } catch (_) {}
        persist();
        renderOnline();
    }, netUnavailable);
}
function joinOnline(code, saved) {
    leaveOnline(true);
    app.netRole = 'guest'; app.me = 1; app.netCode = code; app.netState = 'joining';
    root.dataset.online = '1';
    if (saved && saved.cfg) {
        app.cfg = saved.cfg; app.events = (saved.events || []).slice(); app.s = replay(app.cfg, app.events);
        app.finished = false; app.rq = []; app.remoteFlying = false;
        app.inMode = keypadKind(app.cfg.mode) && real() ? (keypadKind(app.cfg.mode) === 'hits' ? 'hits' : 'total') : 'darts';
        enterGame();
        rebuildBoardDarts();
        nextTurn(false);
    } else {
        showJoin(true);
    }
    feature('dt_online_join', { resume: saved ? 1 : 0 });
    renderOnline();
    loadOnlineDeps().then((O) => {
        if (app.netRole !== 'guest' || app.net) return;
        app.net = O.create(netCallbacks());
        app.net.join(code, (setup.myName || '').trim() || L('player_n', { n: 2 }));
    }, netUnavailable);
}
function leaveOnline(quiet) {
    if (app.net) {
        if (!quiet) { try { app.net.send({ t: 'bye' }); } catch (_) {} }
        try { app.net.destroy(true); } catch (_) {}
    }
    app.net = null; app.netRole = null; app.netCode = null; app.me = null; app.netState = ''; app.rq = []; app.remoteFlying = false;
    delete root.dataset.online;
    if (!quiet) { try { if (/[?&](room|host)=/.test(location.search)) history.replaceState(null, '', location.pathname); } catch (_) {} }
    renderOnline();
}
function showJoin(on) {
    $('dtJoinBox').hidden = !on;
    $('dtSetupNew').hidden = on;
    $('dtJoinText').textContent = L('online_joining');
    fitSoon();
}
let qrFor = '';
function renderOnline() {
    const box = $('dtOnline');
    if (!box) return;
    box.hidden = !app.netRole;
    if (!app.netRole) return;
    const st = app.netState;
    $('dtNetDot').dataset.state = st === 'on' ? 'on' : (st === 'lost' || st === 'error' ? 'bad' : 'wait');
    $('dtNetText').textContent = st === 'on' ? L('online_connected') : st === 'lost' ? L('online_lost') : st === 'error' ? L('online_error')
        : app.netRole === 'host' ? L('online_waiting') : L('online_joining');
    // the invitation: until the guest has come at least once
    const invite = app.netRole === 'host' && st !== 'on' && app.netCode && !(app.events.length && st === 'lost');
    $('dtInvite').hidden = !invite;
    if (invite) {
        const url = inviteUrl();
        $('dtLink').value = url;
        if (qrFor !== url) {
            qrFor = url;
            const N = CFG.net || {};
            const qr = $('dtQr');
            qr.innerHTML = '';
            vendor(N.qr, 'qrcode').then(() => vendor(N.qrAdapter, 'QRCode')).then(() => {
                if (qrFor !== url || !window.QRCode) return;
                qr.innerHTML = '';
                new window.QRCode(qr, { text: url, width: 112, height: 112 });
            }, () => { qr.hidden = true; });
        }
    }
    fitSoon();
}
function bindOnline() {
    $('dtCopy').addEventListener('click', async () => {
        const url = $('dtLink').value;
        try { await navigator.clipboard.writeText(url); window.__toast && window.__toast(L('online_copied'), 'success'); }
        catch (_) { $('dtLink').select(); }
        feature('dt_online_copy');
    });
    $('dtShareLink').addEventListener('click', async () => {
        const url = $('dtLink').value;
        feature('dt_online_share');
        try { if (navigator.share) { await navigator.share({ text: L('online_share_text'), url }); return; } } catch (e) { if (e && e.name === 'AbortError') return; }
        try { await navigator.clipboard.writeText(url); window.__toast && window.__toast(L('online_copied'), 'success'); } catch (_) {}
    });
    $('dtShareLink').hidden = !navigator.share;
}

/* ═══════════════════════════ undo ═══════════════════════════ */
function undo() {
    if (!app.s || !app.events.length) return;
    if (app.netRole) {
        if (!isHumanTurn() || !app.s.visit.length || app.phase === 'flight') return;
        const n = app.events.length - 1;
        applyTrunc(n);
        app.net && app.net.send({ t: 'trunc', n });
        feature('dt_undo', { mode: app.cfg.mode, online: 1 });
        return;
    }
    clearTimeout(app.botTimer);
    cancelAim();
    let evs = app.events.slice();
    if (app.cfg.players.some((p) => p.kind === 'bot')) {
        // against the computer: take back the last dart of a person (and the bot's darts after it)
        const s0 = createMatch(app.cfg);
        const owners = evs.map((ev) => { const who = s0.cur; apply(s0, ev); return who; });
        let i = owners.length - 1;
        while (i >= 0 && !isHumanIdx(owners[i])) i--;
        if (i < 0) return;
        evs = evs.slice(0, i);
    } else {
        evs.pop();
    }
    if (!real()) app.assisted = true;
    app.events = evs;
    app.s = replay(app.cfg, evs);
    app.typed = ''; app.ask = null; $('dtAsk').hidden = true;
    rebuildBoardDarts();
    if (app.cfg.mode === 'free') rebuildHeat();
    persist();
    feature('dt_undo', { mode: app.cfg.mode });
    renderInput();
    nextTurn(false);
}
function isHumanIdx(i) { return app.cfg.players[i] && app.cfg.players[i].kind === 'human'; }
function rebuildBoardDarts() {
    board.clearDarts();
    for (const d of app.s.visit) {
        if (d.x == null && !d.s) continue;
        const pt = d.x != null ? { x: d.x, y: d.y } : aimPoint(d.s, d.m);
        board.placeDart({ x: pt.x, y: pt.y, color: colorOf(app.s.cur) });
    }
}

/* ═══════════════════════════ practice: the group ═══════════════════════════ */
const heat = { points: [], off: [] };
function addHeat(ev) {
    heat.points.push({ x: ev.x, y: ev.y });
    if (ev.ax != null) heat.off.push({ dx: ev.x - ev.ax, dy: ev.y - ev.ay });
    if (heat.points.length > 90) heat.points.shift();
    if (heat.off.length > 90) heat.off.shift();
    board.setHeat({ points: heat.points.slice(), group: null });
}
function rebuildHeat() {
    heat.points = []; heat.off = [];
    app.events.forEach((ev) => { if (ev.k === 'd' && ev.x != null) { heat.points.push({ x: ev.x, y: ev.y }); if (ev.ax != null) heat.off.push({ dx: ev.x - ev.ax, dy: ev.y - ev.ay }); } });
    board.setHeat(heat.points.length ? { points: heat.points.slice(-90), group: null } : null);
}
function groupStats() {
    const o = heat.off;
    if (o.length < 3) return null;
    const mx = o.reduce((a, d) => a + d.dx, 0) / o.length, my = o.reduce((a, d) => a + d.dy, 0) / o.length;
    const r = Math.sqrt(o.reduce((a, d) => a + (d.dx - mx) ** 2 + (d.dy - my) ** 2, 0) / o.length);
    return { spread: Math.round(r * MM), off: Math.round(Math.hypot(mx, my) * MM), dir: Math.atan2(my, mx) };
}

/* ═══════════════════════════ rendering the match ═══════════════════════════ */
function bigOf(p, i) {
    const m = app.cfg.mode;
    if (m === 'x01' || m === 'checkouts') return p.rest;
    if (m === 'cricket') return p.cpts;
    if (m === 'atc') return p.target > 20 ? 'B' : p.target;
    if (m === 'free') return p.st.pts + (app.s.cur === i ? app.s.visit.reduce((a, d) => a + d.pts, 0) : 0);
    return p.score;
}
function metaOf(p) {
    const m = app.cfg.mode, st = p.st;
    const parts = [];
    if (m === 'x01' || m === 'free' || m === 'countup') parts.push(`${L('avg')} <b>${f1(avg3(st))}</b>`);
    if (m === 'x01' && app.cfg.opts.legs > 1) parts.push(`${L('legs_short')} <b>${p.legs}</b>`);
    if (m === 'cricket') { parts.push(`MPR <b>${f1(mpr(st))}</b>`); if (app.cfg.opts.legs > 1) parts.push(`${L('legs_short')} <b>${p.legs}</b>`); }
    const R = roundsOf(app.cfg);
    if (R) parts.push(L('round_n', { n: Math.min(R, p.round + 1), of: R }));
    if (m === 'atc') parts.push(`${L('darts')} <b>${st.darts + (app.s.players[app.s.cur] === p ? app.s.visit.length : 0)}</b>`);
    if (m === 'checkouts' && app.s.drill) parts.push(`${L('closed')} <b>${app.s.drill.closed}/${app.s.drill.nums.length}</b> · ${app.s.drill.i + 1}/${app.s.drill.nums.length}`);
    if (m === 'x01' && real() && app.s.last && app.s.last.pid === app.s.players.indexOf(p) && !app.s.last.bust) parts.push(`+${app.s.last.total}`);
    return parts.join(' · ');
}
function legsDots(p) {
    const need = app.cfg.opts.legs || 1;
    if (need <= 1) return '';
    let h = '';
    for (let i = 0; i < need; i++) h += `<span style="width:7px;height:7px;border-radius:50%;background:${i < p.legs ? 'var(--pc)' : 'var(--lg-border-hi)'}"></span>`;
    return h;
}
function renderPlayers() {
    const s = app.s, el = $('dtPlayers');
    el.dataset.n = String(s.players.length);
    let html = s.players.map((p, i) => `<div class="dt-p${i === s.cur && !s.over ? ' on' : ''}${p.out ? ' out' : ''}" style="--pc:${colorOf(i)}">
        <div class="dt-p-name"><span class="dt-p-turn"></span>${esc(p.name)}${app.netRole && i === app.me ? ` <small style="color:var(--lg-text-3);font-weight:700">· ${esc(L('online_you_tag'))}</small>` : ''}</div>
        <div class="dt-p-legs">${legsDots(p)}</div>
        <div class="dt-p-big">${bigOf(p, i)}</div>
        <div class="dt-p-meta">${metaOf(p)}</div></div>`).join('');
    if (app.cfg.mode === 'cricket') html += cricketGrid();
    el.innerHTML = html;
}
function cricketGrid() {
    const s = app.s;
    const glyph = (n) => (n >= 3 ? '⊗' : n === 2 ? '✕' : n === 1 ? '/' : '·');
    const closedAll = (n) => s.players.every((p) => p.marks[n] >= 3);
    let h = `<div class="dt-cricket" style="grid-column:1/-1"><div class="dt-cr-row head"><span></span>${CRICKET.map((n) => `<span class="${closedAll(n) ? 'closed' : ''}">${n === 25 ? 'B' : n}</span>`).join('')}</div>`;
    s.players.forEach((p, i) => {
        h += `<div class="dt-cr-row" style="--pc:${colorOf(i)}"><span class="dt-cr-dot"></span>${CRICKET.map((n) => `<span class="dt-mk${p.marks[n] >= 3 ? ' c3' : ''}">${glyph(p.marks[n])}</span>`).join('')}</div>`;
    });
    return h + '</div>';
}
function slotHtml(d) {
    if (!d) return '<span class="dt-slot"></span>';
    const cls = !d.s ? 'miss' : (d.s === 25 ? 'bull' : (d.m === 3 ? 'm3' : d.m === 2 ? 'm2' : ''));
    return `<span class="dt-slot f ${cls}">${d.s ? cl(d.s, d.m) : '0'}</span>`;
}
function renderVisit() {
    const s = app.s;
    let darts = s.visit, total = s.visit.reduce((a, d) => a + d.pts, 0), pid = s.cur, bust = false, dim = false;
    if (!darts.length && s.last && s.log.length) {
        const lg = s.log[s.log.length - 1];
        darts = lg.darts.map((d) => ({ s: d.s, m: d.m }));
        total = lg.total; pid = lg.pid; bust = lg.bust; dim = true;
    }
    // a visit typed as a total has no darts to show: the player's name stands in for them
    const typed = dim && !darts.length;
    $('dtSlots').hidden = typed;
    $('dtSlots').innerHTML = [0, 1, 2].map((i) => slotHtml(darts[i])).join('');
    $('dtVisit').style.borderColor = `color-mix(in srgb, ${colorOf(pid)} 45%, transparent)`;
    $('dtVisit').style.opacity = dim ? '0.78' : '1';
    const who = typed ? esc(s.players[pid].name) : L('visit_total');
    const m0 = app.cfg.mode;
    let tot = '';
    if (bust) tot = `<span style="color:var(--lg-danger)">${L('bust')}</span>`;
    else if (m0 === 'cricket') tot = total > 0 ? `+${total}<small>${L('points')}</small>` : '';
    else if (m0 !== 'atc' && m0 !== 'bobs27' && (darts.length || dim)) tot = `${total}<small>${who}</small>`;
    $('dtVTotal').innerHTML = tot;
    // what to throw at
    const r = $('dtRoute');
    let rh = '';
    const m = app.cfg.mode;
    const human = isHumanTurn();
    if ((m === 'x01' || m === 'checkouts') && !s.over && (settings.hints || real())) {
        const p = s.players[s.cur];
        const rt = hint(s);
        if (rt && (human || real())) {
            rh = `<span class="dt-route-l">${L('checkout')}</span>` + rt.map((d) => `<span class="dt-chip">${cl(d.s, d.m)}</span>`).join('<span class="dt-arrow">›</span>');
        } else if (human && p.rest <= 170 && route(p.rest, 3, m === 'checkouts' ? 'double' : app.cfg.opts.outRule) && s.visit.length) {
            rh = `<span class="dt-route-l">${L('no_route', { n: 3 - s.visit.length })}</span>`;
        } else if (human && p.opened && p.rest > 170) {
            // the scoring phase: the treble 20 is where the points are
            rh = `<span class="dt-route-l">${L('target')}</span><span class="dt-chip dim">T20</span>`;
        }
    } else if (!s.over && m !== 'cricket' && m !== 'free' && m !== 'x01') {
        const t = target(s);
        if (t) rh = `<span class="dt-route-l">${L('target')}</span><span class="dt-chip">${t.s === 25 ? L('bull') : (t.m === 2 ? 'D' + t.s : t.s)}</span>`;
    } else if (m === 'free') {
        const g = groupStats();
        if (g) rh = `<span class="dt-chip dim">${L('spread')} ${L('mm', { n: g.spread })}</span><span class="dt-chip dim">${L('offset')} ${L('mm', { n: g.off })}</span>`;
        if (heat.points.length) rh += `<button type="button" class="dt-chip dim" data-act="heat-clear" style="cursor:pointer">${esc(L('heat_clear'))}</button>`;
    }
    r.innerHTML = rh;
    // the board: the next bed lit (hints), only for a person aiming on the screen
    if (!real() && human && settings.hints && !s.over) {
        let beds = null;
        if (m === 'x01' || m === 'checkouts') { const rt = hint(s); if (rt) beds = [{ s: rt[0].s, m: rt[0].m, outer: rt[0].m === 1 }]; }
        else if (m !== 'cricket' && m !== 'free') { const t = target(s); if (t) beds = [{ s: t.s, m: t.m }]; }
        board.setHighlight(beds);
    } else board.setHighlight(null);
}
function renderPips() {
    const used = app.s ? app.s.visit.length : 0;
    const col = app.s ? colorOf(app.s.cur) : PLAYER_COLORS[0];
    $('dtPips').querySelectorAll('.dt-pip').forEach((p, i) => { p.classList.toggle('used', i < used); p.style.setProperty('--dt-pc', col); });
}
function renderStatus() {
    const s = app.s;
    const st = $('dtStatus');
    if (!s) { st.textContent = ''; return; }
    if (s.over) { st.textContent = ''; return; }
    const p = s.players[s.cur];
    const mine = app.netRole ? s.cur === app.me : p.name === L('you');
    const who = s.players.length > 1 ? (mine ? L('turn_you') : L('turn_of', { name: p.name })) : '';
    const dn = L('dart_n', { n: s.visit.length + 1 });
    let how = '';
    if (app.netRole) {
        if (app.netState !== 'on') { st.textContent = app.netState === 'lost' ? L('online_lost') : (app.netRole === 'host' ? L('online_waiting') : L('online_joining')); return; }
        if (s.cur !== app.me) { st.textContent = L('online_wait_turn', { name: p.name }); return; }
    }
    if (app.phase === 'bot') { st.innerHTML = esc(L('bot_throws', { name: p.name })); return; }
    if (real()) {
        how = app.inMode === 'darts' ? L('real_hint_darts', { n: s.visit.length + 1 }) : (app.inMode === 'hits' ? L('input_hits') : L('real_hint_total', { name: p.name }));
        st.innerHTML = (who ? `<b>${esc(who)}</b> · ` : '') + esc(how);
        return;
    }
    if (settings.throwStyle === 'sweep') how = aim.sweep ? (aim.sweep.phase === 'x' ? L('sweep_x') : aim.sweep.phase === 'y' ? L('sweep_y') : L('sweep_go')) : '';
    // how to throw: until the person has thrown once — after that the line is the turn alone
    else if (!app.taught) how = aim.keys ? L('aim_keys') : (matchMedia('(pointer: coarse)').matches ? L('aim_touch') : L('aim_mouse'));
    st.innerHTML = (who ? `<b>${esc(who)}</b> · ` : '') + esc(dn) + (how ? ' — ' + esc(how) : '');
}
function renderActions() {
    const s = app.s;
    $('dtThrowBtn').hidden = !(s && !real() && settings.throwStyle === 'sweep');
    let can = !!(s && app.events.length && !s.over);
    if (can && app.netRole) can = isHumanTurn() && s.visit.length > 0;
    if (can && app.cfg.players.some((p) => p.kind === 'bot')) can = app.events.length > 0 && (isHumanTurn() || app.phase === 'between');
    $('dtUndo').disabled = !can;
}
function renderMatchStats(el) {
    const s = app.s;
    if (!s) { el.innerHTML = ''; return; }
    const m = app.cfg.mode;
    const rows = [];
    const P = s.players;
    const col = (fn) => P.map((p) => `<td>${fn(p)}</td>`).join('');
    if (m === 'x01' || m === 'checkouts' || m === 'countup' || m === 'free') rows.push([L('avg3'), col((p) => f1(avg3(p.st)))]);
    if (m === 'x01') {
        rows.push([L('first9'), col((p) => f1(first9(p.st)))]);
        rows.push(['180', col((p) => p.st.t180)]);
        rows.push(['140+', col((p) => p.st.t140)]);
        rows.push(['100+', col((p) => p.st.t100)]);
        rows.push([L('co_pct'), col((p) => { const c = checkoutPct(p.st); return c == null ? '—' : `${Math.round(c)}% <small style="color:var(--lg-text-3)">${p.st.dblHit}/${p.st.dblAtt}</small>`; })]);
        rows.push([L('hi_co'), col((p) => p.st.hiCo || '—')]);
        if (app.cfg.opts.legs > 1) rows.push([L('best_leg'), col((p) => p.st.bestLeg || '—')]);
    }
    if (m === 'cricket') rows.push([L('mpr'), col((p) => f1(mpr(p.st)))]);
    if (roundsOf(app.cfg) || m === 'atc') rows.push([L('hits'), col((p) => p.st.hits)]);
    rows.push([L('darts'), col((p) => p.st.darts + (P[s.cur] === p ? s.visit.length : 0))]);
    const head = P.length > 1 ? `<thead><tr><th></th>${P.map((p, i) => `<th style="--pc:${colorOf(i)}"><i></i>${esc(p.name)}</th>`).join('')}</tr></thead>` : '';
    el.innerHTML = `<table class="dt-stbl">${head}<tbody>${rows.map((r) => `<tr><th scope="row">${r[0]}</th>${r[1]}</tr>`).join('')}</tbody></table>`;
}
function renderLog() {
    const s = app.s, el = $('dtLog');
    if (!s) { el.innerHTML = ''; return; }
    const items = s.log.slice(-60).reverse().map((v) => {
        const darts = v.darts.length ? v.darts.map((d) => cl(d.s, d.m)).join(' ') : (v.note && v.note.startsWith('h') ? L('hits') + ': ' + v.note.slice(1) : '—');
        const after = v.rest != null ? v.rest : (app.cfg.mode === 'cricket' ? '' : v.score);
        const cls = v.bust ? 'bust' : (v.note === 'leg' || v.note === 'shanghai' ? 'leg' : '');
        return `<li class="${cls}" style="--pc:${colorOf(v.pid)}"><i></i><span>${esc(s.players[v.pid].name)} · ${darts}</span><b>${v.bust ? '✕' : v.total}</b><em>${after === '' ? '' : after}</em></li>`;
    });
    el.innerHTML = items.join('') || `<li style="display:block;background:none" class="dt-empty">—</li>`;
}
function renderMatch() {
    if (!app.s) return;
    renderPlayers();
    renderVisit();
    renderPips();
    renderStatus();
    renderActions();
    if (!$('dtMStats').hidden || app.layout === 'xwide') renderMatchStats($('dtMStats'));
    if (!$('dtLogPanel').hidden || app.layout === 'xwide') renderLog();
    if (real()) renderKeypad();
}
function bindMatchTabs() {
    const tabs = [...document.querySelectorAll('#dtMTabs .dt-ptab')];
    const sel = (k) => {
        tabs.forEach((t) => { const on = t.dataset.tab === k; t.setAttribute('aria-selected', on ? 'true' : 'false'); t.tabIndex = on ? 0 : -1; });
        $('dtMStats').hidden = k !== 'stats';
        $('dtLogPanel').hidden = k !== 'log';
        renderMatch();
    };
    tabs.forEach((t) => t.addEventListener('click', () => sel(t.dataset.tab)));
    $('dtMTabs').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const i = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        const nx = tabs[(i + 1) % tabs.length];
        sel(nx.dataset.tab); nx.focus(); e.preventDefault();
    });
    $('dtUndo').addEventListener('click', undo);
    $('dtRoute').addEventListener('click', (e) => {
        if (!e.target.closest('[data-act="heat-clear"]')) return;
        heat.points = []; heat.off = [];
        board.setHeat(null);
        renderVisit();
        feature('dt_heat_clear');
    });
    $('dtThrowBtn').addEventListener('click', () => { snd.unlock(); sweepPress(); });
}

/* ═══════════════════════════ the end of a match ═══════════════════════════ */
function finishMatch() {
    clearTimeout(app.botTimer);
    cancelAim();
    app.phase = 'over';
    const s = app.s, m = app.cfg.mode;
    if (app.finished) { renderMatch(); return; }
    app.finished = true;
    const me = s.players[meIdx()];
    let title, sub = '';
    const won = s.winner === meIdx() && !s.draw;
    if (s.draw) title = L('draw');
    else if (s.players.length === 1) title = L('result');
    else if (oppKind() === 'bot' || app.netRole) title = won ? L('win_you') : L('lose_you');
    else title = L('win_name', { name: s.players[s.winner].name });
    if (m === 'x01' || m === 'cricket') sub = s.players.map((p) => p.legs).join(' : ');
    else if (m === 'checkouts' && s.drill) sub = L('drill_result', { n: s.drill.closed, of: s.drill.nums.length });
    else if (m === 'atc') sub = `${L('darts')}: ${me.st.darts}`;
    else sub = s.players.map((p) => `${p.name}: ${p.score}`).join(' · ');
    const badges = recordMatch(won);
    if (app.assisted) sub += ' · ' + L('assisted');
    $('dtEndTitle').textContent = title;
    $('dtEndSub').textContent = sub;
    $('dtEndBadges').innerHTML = badges.map((b) => `<span class="dt-badge">★ ${esc(b)}</span>`).join('');
    const tbl = document.createElement('div');
    renderMatchStats(tbl);
    $('dtEndTable').innerHTML = '';
    $('dtEndTable').appendChild(tbl);
    clearSave();
    stage('complete', { mode: m });
    try {
        window.__aeToolComplete && window.__aeToolComplete({ mode: m, where: app.cfg.where, opp: oppKind(), result: s.draw ? 'draw' : (won ? 'win' : 'loss'),
            avg: Math.round(avg3(me.st) * 10) / 10, darts: me.st.darts, assisted: app.assisted ? 1 : 0, ms: Date.now() - app.startedAt });
    } catch (_) {}
    try { window.__wakeLock && window.__wakeLock.release(); } catch (_) {}
    renderMatch();
    setTimeout(() => { const d = $('dtEnd'); try { if (!d.open) d.showModal(); } catch (_) { d.setAttribute('open', ''); } }, 900);
}
function bindEnd() {
    const d = $('dtEnd');
    const close = () => { try { d.close(); } catch (_) { d.removeAttribute('open'); } };
    $('dtAgain').addEventListener('click', () => {
        close();
        if (app.netRole === 'guest') {
            // the host's copy is the match: ask it for one more
            app.net && app.net.send({ t: 'again' });
            $('dtStatus').textContent = L('online_again_wait');
            feature('dt_again', { mode: app.cfg.mode, online: 1 });
            return;
        }
        const cfg = Object.assign({}, app.cfg, { at: Date.now() });
        if (cfg.mode === 'checkouts' && !cfg.opts.daily) cfg.opts = Object.assign({}, cfg.opts, { seed: ((Math.random() * 1e9) | 0) + 1 });
        feature('dt_again', { mode: cfg.mode });
        startMatch(cfg);
    });
    $('dtOther').addEventListener('click', () => { close(); if (app.netRole) { leaveOnline(); clearSave(); } app.s = null; leaveGame(); });
    $('dtShare').addEventListener('click', shareResult);
    $('dtRulesClose').addEventListener('click', () => { try { $('dtRules').close(); } catch (_) {} });
}
async function shareResult() {
    const s = app.s;
    if (!s) return;
    const me = s.players[0];
    const result = $('dtEndTitle').textContent + ($('dtEndSub').textContent ? ' (' + $('dtEndSub').textContent + ')' : '');
    const text = L('share_text', { mode: L('mode_' + app.cfg.mode), result, avg: f1(avg3(me.st)) });
    const url = location.origin + location.pathname;
    feature('dt_share', { mode: app.cfg.mode });
    try { if (navigator.share) { await navigator.share({ text, url }); return; } } catch (e) { if (e && e.name === 'AbortError') return; }
    try { await navigator.clipboard.writeText(text + ' ' + url); window.__toast && window.__toast(L('copied'), 'success'); } catch (_) {}
}
function showRules() {
    const m = app.cfg ? app.cfg.mode : setup.mode;
    $('dtRulesTitle').textContent = L('mode_' + m);
    $('dtRulesText').textContent = L('rules_' + m);
    try { $('dtRules').showModal(); } catch (_) { $('dtRules').setAttribute('open', ''); }
    feature('dt_rules', { mode: m });
}

/* ═══════════════════════════ records and history ═══════════════════════════ */
function readStats() {
    const s = load('stats', null);
    if (!s || s.v !== 1) return { v: 1, played: 0, won: 0, best: {}, hist: [], dbls: {} };
    s.best = s.best || {}; s.hist = s.hist || []; s.dbls = s.dbls || {};
    return s;
}
function recordMatch(won) {
    const s = app.s, m = app.cfg.mode, me = s.players[meIdx()], st = me.st;
    const all = readStats();
    const badges = [];
    // records belong to the person on this device: matches against the computer or alone
    const mine = oppKind() !== 'friends';
    all.played++;
    if (won) all.won++;
    const better = (k, v, lower) => {
        if (v == null || !isFinite(v) || v <= 0 && !lower) return;
        const was = all.best[k];
        if (was == null || (lower ? v < was : v > was)) { all.best[k] = v; if (was != null) badges.push(L('new_record')); }
    };
    if (mine && !app.assisted) {
        if (m === 'x01' && st.darts >= 9) better('avg', Math.round(avg3(st) * 10) / 10);
        if (m === 'x01') better('co', st.hiCo);
        if (m === 'x01') all.best.t180 = (all.best.t180 || 0) + st.t180;
        if (m === 'atc' && s.winner === meIdx()) better('atc', st.darts, true);
        if (m === 'sector20' || m === 'countup' || m === 'biground' || m === 'bobs27') better(m, me.score);
        if (m === 'checkouts' && s.drill) better('checkouts', s.drill.closed);
        // doubles: every dart thrown at a one-dart finish, per double
        Object.keys(st.dbls || {}).forEach((k) => {
            const b = all.dbls[k] || (all.dbls[k] = [0, 0]);
            b[0] += st.dbls[k][0]; b[1] += st.dbls[k][1];
        });
    }
    all.hist.unshift({ t: Date.now(), m, w: won ? 1 : 0, d: s.draw ? 1 : 0, n: s.players.length, o: oppKind(), r: app.cfg.where === 'real' ? 1 : 0,
        a: Math.round(avg3(st) * 10) / 10, sc: (m === 'x01' || m === 'cricket') ? s.players.map((p) => p.legs).join(':') : (m === 'checkouts' && s.drill ? s.drill.closed : me.score),
        x: app.assisted ? 1 : 0 });
    if (all.hist.length > 200) all.hist.length = 200;
    if (m === 'checkouts' && app.cfg.opts.daily && s.drill) {
        all.daily = all.daily || {};
        const k = dailyKey();
        if (all.daily[k] == null) all.daily[k] = s.drill.closed;
    }
    save('stats', all);
    return [...new Set(badges)];
}

function renderAllTime(el) {
    const all = readStats();
    const b = all.best;
    // the records a person has: the core four always, the rest once there is one
    const rec = (v, l, always) => (v == null && !always ? '' : `<div class="dt-rec"><b>${v == null ? '—' : v}</b><small>${l}</small></div>`);
    let html = `<div class="dt-sect"><div class="dt-h">${L('records')}</div><div class="dt-recs">`
        + rec(all.played, L('rec_games'), true) + rec(all.won, L('rec_wins'), true)
        + rec(b.avg != null ? f1(b.avg) : null, L('rec_avg'), true) + rec(b.co, L('rec_co'), true) + rec(b.t180 || null, L('rec_180'))
        + rec(b.atc, L('rec_atc')) + rec(b.sector20, L('rec_sector20')) + rec(b.countup, L('rec_countup')) + rec(b.biground, L('rec_biground'))
        + rec(b.bobs27, L('rec_bobs27')) + rec(b.checkouts, L('rec_checkouts')) + '</div></div>';
    // doubles and the trend: Premium where it is sold
    const locked = proLocked();
    html += `<div class="dt-sect"><div class="dt-h">${L('doubles')}</div>`;
    const dbls = Object.keys(all.dbls).length ? all.dbls : null;
    const keys = [20, 16, 8, 18, 12, 10, 4, 14, 6, 2, 19, 17, 15, 13, 11, 9, 7, 5, 3, 1, 25];
    const grid = `<div class="dt-dbls">${keys.map((k) => {
        const v = (dbls && dbls[k]) || [0, 0];
        const pct = v[0] ? Math.round((v[1] / v[0]) * 100) : null;
        const bg = pct == null ? '' : `background:color-mix(in srgb, ${pct >= 40 ? 'var(--lg-success)' : pct >= 20 ? '#facc15' : 'var(--lg-danger)'} ${Math.min(60, 12 + v[0] * 3)}%, var(--lg-surface-2))`;
        return `<div class="dt-dbl" style="${bg}"><b>${k === 25 ? 'B' : 'D' + k}</b><span>${pct == null ? '—' : pct + '%'}</span><small>${v[1]}/${v[0]}</small></div>`;
    }).join('')}</div>`;
    const trend = trendSvg(all.hist.filter((h) => h.m === 'x01' && h.a > 0).slice(0, 40).reverse());
    const body = (dbls ? grid : `<p class="dt-empty">${L('doubles_empty')}</p>`) + `<p class="dt-note">${L('doubles_note')}</p>`
        + `<div class="dt-h" style="margin-top:10px">${L('trend')}</div>` + trend;
    if (locked) html += `<div class="dt-locked"><div class="dt-lockbody">${body}</div><div class="dt-lock"><p>${L('pro_stats_note')}</p><a class="tc-btn sm" href="${esc(PRO.url || '#')}" data-pro="stats">${L('pro_cta')}</a></div></div>`;
    else html += body;
    html += '</div>';
    html += `<div class="dt-sect"><div class="dt-h">${L('history')}</div>`;
    if (!all.hist.length) html += `<p class="dt-empty">${L('history_empty')}</p>`;
    else {
        const df = (() => { try { return new Intl.DateTimeFormat(window.__intlLocale ? window.__intlLocale() : undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (_) { return null; } })();
        html += '<ol class="dt-hist">' + all.hist.slice(0, 8).map((h) => `<li><b>${L('mode_' + h.m)}</b><span class="${h.w ? 'w' : 'l'}">${h.d ? L('draw') : (h.n > 1 ? (h.w ? '✓ ' : '') + h.sc : h.sc)}</span>`
            + `<small>${df ? df.format(new Date(h.t)) : ''}${h.a ? ' · ' + L('avg') + ' ' + f1(h.a) : ''}${h.r ? ' · ' + L('board_real') : ''}</small></li>`).join('') + '</ol>';
    }
    html += `<button type="button" class="dt-link" id="dtResetStats">${L('reset_stats')}</button></div>`;
    el.innerHTML = html;
    const pro = el.querySelector('[data-pro]');
    if (pro) pro.addEventListener('click', () => feature('dt_pro_click', { from: 'stats' }));
    const rs = el.querySelector('#dtResetStats');
    if (rs) rs.addEventListener('click', () => confirmDlg(L('reset_confirm'), () => { save('stats', null); renderAllTime(el); feature('dt_stats_reset'); }));
}
function trendSvg(hs) {
    if (hs.length < 2) return `<p class="dt-empty">${L('history_empty')}</p>`;
    const W = 300, H = 110, pad = 6;
    const vs = hs.map((h) => h.a);
    const lo = Math.max(0, Math.floor(Math.min(...vs) / 10) * 10), hi = Math.ceil(Math.max(...vs) / 10) * 10 || 10;
    const x = (i) => pad + (i / (hs.length - 1)) * (W - pad * 2);
    const y = (v) => H - pad - ((v - lo) / Math.max(1, hi - lo)) * (H - pad * 2);
    const pts = vs.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
    return `<svg class="dt-trend" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(L('trend'))}"><polyline points="${pts}" fill="none" stroke="var(--lg-accent)" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>`
        + vs.map((v, i) => `<circle cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="2.6" fill="var(--lg-accent)"/>`).join('')
        + `<text x="${W - pad}" y="12" text-anchor="end" font-size="12" fill="currentColor" opacity=".6">${hi}</text><text x="${W - pad}" y="${H - 2}" text-anchor="end" font-size="12" fill="currentColor" opacity=".6">${lo}</text></svg>`;
}
function confirmDlg(text, yes) {
    const d = $('dtConfirm');
    $('dtConfirmText').textContent = text;
    const ok = $('dtConfirmYes'), no = $('dtConfirmNo');
    const done = (v) => { ok.onclick = no.onclick = null; try { d.close(); } catch (_) {} if (v) yes(); };
    ok.onclick = () => done(true);
    no.onclick = () => done(false);
    try { d.showModal(); } catch (_) { d.setAttribute('open', ''); }
}

/* ═══════════════════════════ saving a match in progress ═══════════════════════════ */
const SAVE_KEY = 'match';
function persist() {
    if (!app.s || app.s.over || !app.events.length) { if (app.s && app.s.over) clearSave(); return; }
    save(SAVE_KEY, { v: 1, t: Date.now(), cfg: app.cfg, events: app.events, assisted: app.assisted,
        net: app.netRole ? { role: app.netRole, code: app.netCode, me: app.me } : null });
}
function clearSave() { save(SAVE_KEY, null); }
function readSave() {
    const d = load(SAVE_KEY, null);
    if (!d || d.v !== 1 || !d.cfg || !Array.isArray(d.events) || !d.events.length) return null;
    if (Date.now() - (d.t || 0) > 1000 * 3600 * 24 * 14) return null;
    return d;
}
function resumeMatch() {
    const d = readSave();
    if (!d) return;
    if (d.net && d.net.role === 'host' && d.net.code) { startOnlineHost({ cfg: d.cfg, events: d.events, code: d.net.code }); return; }
    if (d.net && d.net.role === 'guest' && d.net.code) { joinOnline(d.net.code, d); return; }
    try {
        app.cfg = d.cfg; app.events = d.events.slice(); app.assisted = !!d.assisted;
        app.s = replay(app.cfg, app.events);
        if (app.s.over) { clearSave(); app.s = null; renderSetup(); return; }
        app.startedAt = Date.now(); app.firstThrow = true;
        app.inMode = keypadKind(app.cfg.mode) && real() ? (keypadKind(app.cfg.mode) === 'hits' ? 'hits' : 'total') : 'darts';
        enterGame();
        rebuildBoardDarts();
        if (app.cfg.mode === 'free') rebuildHeat();
        stage('resumed', { mode: app.cfg.mode });
        nextTurn(false);
    } catch (e) {
        clearSave();
        if (window.__handleError) window.__handleError(e, 'darts_resume', { say: '' });
    }
}

/* ═══════════════════════════ boot ═══════════════════════════ */
function boot() {
    bindOnline();
    bindSetup();
    bindMenu();
    bindBoard();
    bindKeys();
    bindKeypad();
    bindMatchTabs();
    bindEnd();
    renderSetup();
    ensureWide();
    fit(true);
    if (!window.WideWorkspace) {
        // the wide-screen primitive is a deferred script: take the width when it arrives
        let tries = 0;
        const iv = setInterval(() => { if (window.WideWorkspace || ++tries > 40) { clearInterval(iv); ensureWide(); fit(true); } }, 100);
    }
    let rT = 0;
    window.addEventListener('resize', () => { clearTimeout(rT); rT = setTimeout(() => fit(false), 60); });
    window.addEventListener('pagehide', persist);
    document.addEventListener('visibilitychange', () => { if (document.hidden) persist(); });
    // the next frame, not inside the observer: resizing the console from its own callback
    // is the «ResizeObserver loop» warning (WebKit reports it as an error)
    let roRaf = 0;
    if (window.ResizeObserver) { try { new ResizeObserver(() => { if (!roRaf) roRaf = requestAnimationFrame(() => { roRaf = 0; fit(false); }); }).observe(root); } catch (_) {} }
    stage('ready', { layout: app.layout });
    // a link: ?room=CODE joins the host's match; ?host=CODE is the host's own page after a reload
    try {
        const q = new URLSearchParams(location.search);
        const room = (q.get('room') || '').toLowerCase();
        const host = (q.get('host') || '').toLowerCase();
        const saved = readSave();
        if (/^[a-z0-9]{6,8}$/.test(room)) joinOnline(room, saved && saved.net && saved.net.role === 'guest' && saved.net.code === room ? saved : null);
        else if (/^[a-z0-9]{6,8}$/.test(host) && saved && saved.net && saved.net.role === 'host' && saved.net.code === host) startOnlineHost({ cfg: saved.cfg, events: saved.events, code: host });
        else if (host) history.replaceState(null, '', location.pathname);
    } catch (_) {}
}

try { boot(); } catch (e) {
    root.setAttribute('data-fit', '1');
    if (window.__handleError) window.__handleError(e, 'darts_boot');
}
